import { Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild, Inject } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { HttpService } from 'src/app/services/http.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Router, ActivatedRoute, NavigationStart } from '@angular/router';
import { URLUtils } from 'src/app/urlUtils';
import { ConfirmationDialogService } from '../confirmation-dialog/confirmation-dialog.service';
import { ensureActiveSubscription } from '../shared/subscription-dialog';
import { Subscription, Subject } from 'rxjs';
import { MarketplaceVisibilityService } from '../services/marketplace-visibility.service';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

interface DaySchedule {
  date: string;
  day_of_week: number;
  day_name: string;
  date_label: string;
  is_working_day: boolean;
  work_slots: { start_time: string; end_time: string }[];
}

@Component({
    selector: 'app-firm-profile',
    templateUrl: './firm-profile.component.html',
    styleUrls: ['./firm-profile.component.scss'],
    standalone: false
})
export class FirmProfileComponent implements OnInit, OnDestroy {
  // Profile data
  profile_details: any;
  basic_profile: any;
  firm_details: any;
  subscription_details: any;

  
  private _routerSub: Subscription | null = null;

  // "Available for Consultations" — always driven by MarketplaceVisibilityService,
  // which is itself seeded from is_marketplace_listed on the login/profile response.
  isMarketplaceListed: boolean = true;
  private _marketplaceSub: Subscription | null = null;

  private _savedPersonalInfo: any = null;
  private _savedPracticeDetails: any = null;
  private _savedEducation: any = null;
  private _savedAvailability: any = null;
  // Bio expansion state
isBioExpanded: boolean = false;
// Character threshold for showing read more button
private readonly BIO_CHAR_THRESHOLD = 100;
  // Profile picture
  profilePictureUrl: string | null = null;
  // Holds the locally-selected/cropped preview shown ONLY inside the Set
  // Profile Photo modal. The main page must keep showing `profilePictureUrl`
  // (the actual saved photo) until the upload succeeds — it should never
  // reflect an in-progress selection.
  modalPreviewUrl: any = null;
  
  // Tab management
  activeTab: string = 'practice';
  selectedMatter: string = 'basicprofile';
  practiceAreaSearch = '';
  
  // Mode management
  isEditPersonalInfo: boolean = false;
  isEditAdditionalInfo: boolean = false;
  isEditProfilePic: boolean = false;
  isEdit: boolean = false;
  isDisplay: boolean = true;
  submitted: boolean = false;
  
  // Forms
  personalInfoForm!: FormGroup;
  practiceDetailsForm!: FormGroup;
  educationForm!: FormGroup;
  availabilityForm!: FormGroup;
  
  consultationFeeOptions: number[] = [500, 600, 700, 800, 900, 1000, 1100, 1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000];

  // Currency list
  currencyList = [
    { value: 'USDollar(USD)', name: 'USDollar(USD)' },
    { value: 'Euro(EUR)', name: 'Euro(EUR)' },
    { value: 'JapaneseYen(JPY)', name: 'JapaneseYen(JPY)' },
    { value: 'Pound(GBP)', name: 'Pound(GBP)' },
    { value: 'AustralianDollar(AUD)', name: 'AustralianDollar(AUD)' },
    { value: 'CanadianDollar(CAD)', name: 'CanadianDollar(CAD)' },
    { value: 'SwissFranc(CHF)', name: 'SwissFranc(CHF)' },
    { value: 'KuwaitiDinar(KWD)', name: 'KuwaitiDinar(KWD)' },
    { value: 'BahrainiDinar(BHD)', name: 'BahrainiDinar(BHD)' },
    { value: 'IndianRupee(INR)', name: 'IndianRupee(INR)' }
  ];
  
  // Countries
  countries: any[] = [];
  
  // Practice areas and services (predefined lists)
  practiceAreasList :any[]=[];
  isAdjustingPic = false;
  // Set Profile Photo modal state
  isPhotoModalOpen: boolean = false;
  photoModalStep: 'select' | 'crop' = 'select';

  // WhatsApp-style avatar interaction: contextual dropdown + remove confirmation
  showAvatarMenu: boolean = false;
  showRemoveConfirm: boolean = false;
  highlightAvatar: boolean = false;
  showAvatarHint: boolean = false;
  adjustX = 0;
  adjustY = 0;
  adjustScale = 1;
  adjustRotation = 0;
  adjustPreviewUrl: SafeUrl | null = null;

  private _pendingUploadFile: File | null = null;
  private _savedAdjustX = 0;
  private _savedAdjustY = 0;
  private _savedAdjustScale = 1;
  private _savedAdjustRotation = 0;
  private _dragging = false;
  private _dragStartX = 0;
  private _dragStartY = 0;
  private _dragOriginX = 0;
  private _dragOriginY = 0;
  
  // Services Offered — populated via API (suggest + search)
  servicesList: string[] = [];

  // Suggested services from /professional/v3/services/suggest (based on practice areas)
  suggestedServices: string[] = [];
  hasSuggestedServices: boolean = false; // true only when API returns actual service_suggestions (not a fallback)

  // Whether the services dropdown is in "search" mode vs "suggested" mode
  serviceSearchMode: boolean = false;

  // Loading state for service API calls
  isLoadingServices: boolean = false;

  // Subject for debounced service search (US-3)
  private serviceSearch$ = new Subject<string>();
  private _serviceSearchSub: Subscription | null = null;

  // Whether to show the "Other" free-text entry row
  showOtherServiceInput: boolean = false;

  // Bound to the free-text "Other" input
  otherServiceValue: string = '';

  // All predefined services — populated from API response (all_services key).
  // Used for "View More Services" and duplicate detection.
  allPredefinedServices: string[] = [];

  // Whether the "View More Services" section is expanded
  showAllServices: boolean = false;

  // Predefined services matching user's current "Other" input (for suggestion panel)
  otherServiceSuggestions: string[] = [];

  // Exact duplicate found when user clicks Add (for duplicate warning)
  duplicateServiceMatch: string = '';
  
  languagesList = [
    'English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam',
    'Bengali', 'Marathi', 'Gujarati', 'Punjabi', 'Urdu', 'Spanish',
    'French', 'German', 'Mandarin', 'Arabic'
  ];
  // Input values for chip inputs
practiceAreaInput: string = '';
serviceInput: string = '';
languageInput: string = '';

// Dropdown visibility flags
showPracticeAreaDropdown: boolean = false;
showServiceDropdown: boolean = false;
showLanguageDropdown: boolean = false;

// Gender custom dropdown (Profile Information — solo & firm accounts)
isGenderDropdownOpen: boolean = false;
genderOptions: { value: string; label: string }[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
];

// Filtered lists for dropdowns
filteredPracticeAreas: string[] = [];
filteredServices: string[] = [];
filteredLanguages: string[] = [];

// Court custom dropdown state
activeCourtStateDropdown: number | null = null;
activeCourtCityDropdown: number | null = null;
activeCourtNameDropdown: number | null = null;
activeCourtTypeDropdown: number | null = null;
courtStateSearch: string[] = [];
courtCitySearch: string[] = [];
/** Loading indicator while /v3/court/suggest is being fetched */
isLoadingCourtTypes: boolean = false;
courtNameSearch: string[] = [];
  // Days of week
  daysOfWeek = [
    { value: 0, name: 'Monday' },
    { value: 1, name: 'Tuesday' },
    { value: 2, name: 'Wednesday' },
    { value: 3, name: 'Thursday' },
    { value: 4, name: 'Friday' },
    { value: 5, name: 'Saturday' },
    { value: 6, name: 'Sunday' }
  ];
  
  currentYear = new Date().getFullYear();
  today: Date = new Date(); // used to restrict DOB datepicker to past dates only
  showExcludePanel: boolean = false;
  currentExcludeDay: number = -1;
  activeExcludeDayIndex: number | null = null;

  excludeSlots: { [key: number]: string[] } = {};
  tempSelectedExcludeSlots: string[] = [];
  availableExcludeSlots: { start: string; end: string }[] = []; 
  MAX_EXCLUDE_SLOTS = 4;
  MIN_AVAILABILITY_HOURS = 4; 
  timeOptions: string[] = [];
  // Patterns
  emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  phonePattern = /^[0-9]{10}$/;
  zipcode5Pattern = /^[0-9]{5}$/;
  zipcode6Pattern = /^[0-9]{6}$/;
  websitePattern = /^(https?:\/\/)?([\w.-]+)\.[a-zA-Z]{2,}.*$/;
  
  // Product condition
  product = this.getProductFromStorage();
  isLauditor = this.product === 'lauditor';
  isSoloCategory: boolean = false;
  caseTypes: string[] = [];               // from API

  // ── Court Practice Details ─────────────────────────────
  /** States and cities data fetched from /professional/v3/court/states */
  courtStatesData: { state: string; cities: string[] }[] = [];
  /** Suggested court types from /professional/v3/court/suggest (API-driven) */
  courtSuggestedTypes: string[] = [];
  /** Full list of all court types from all_courts (API-driven) */
  courtAllPredefinedTypes: string[] = [];
  /** True only when API returns real court_suggestions (not empty fallback) */
  hasSuggestedCourtTypes: boolean = false;
  /** Per-row flag: index of the row with expanded "View All Courts" state, or null */
  showAllCourtTypes: number | null = null;
  /** Search term for court type dropdown per row */
  courtTypeSearch: string[] = [];
  /** Whether "Other" city input is shown per row */
  showCityOtherInput: boolean[] = [];
  /** Free-text "Other" city value per row */
  courtCityOtherInput: string[] = [];
  /** Input for "Types of Cases Handled" chip field */
  caseHandledInput: string = '';
  editingCaseIndex: number = -1;  // -1 means not editing, ≥0 means editing that index

  // Role-based access control
  userRole: string = '';

  /** True when the logged-in user is a Team Member */
  get isTM(): boolean { return this.userRole === 'TM'; }
  /** True when the logged-in user is a Group Head */
  get isGH(): boolean { return this.userRole === 'GH'; }
  /** True when the logged-in user is an Associate Accounts Manager */
  get isAAM(): boolean { return this.userRole === 'AAM'; }
  /** True when the logged-in user is a Super User */
  get isSU(): boolean { return this.userRole === 'SU'; }
  /** True for TM or GH — same profile view rules apply */
  get isTMorGH(): boolean { return this.isTM || this.isGH; }
  /** True when website field should be disabled: entity category users who are not SU or AAM */
  get isWebsiteDisabled(): boolean { return !this.isSoloCategory && !this.isSU && !this.isAAM; }

  /**
   * For SU role: true when viewing "Firm Profile" submenu (profileType=firm),
   * false when viewing "My Profile" submenu (profileType=personal or absent).
   * For all other roles: always false.
   */
  isFirmProfileView: boolean = false;


  MAX_PRACTICE_AREAS = 5;
  // Profile completion
  profileCompletionPercentage: number = 0;
  showProfileBanner: boolean = false;

  // Login identifier tracking: 'email' | 'mobile'
  loginIdentifierType: string = '';
  loggedInIdentifier: string = '';
  bioSummary: any;
  isBioLoading = false;


  suggestedPracticeAreas: string[] = [];
  allPredefinedPracticeAreas: string[] = [];
  showAllPracticeAreas = false;
  practiceAreaSearchMode = false;
   // ─── Plan modal ───────────────────────────────────────────────────────────
  profuid = localStorage.getItem('profuid')
  showPlanModal: boolean          = false;
  planModalMode: 'upgrade' | 'downgrade' | null = null;
  planModalLoading: boolean       = false;
  planModalData: any              = null;
  planOptions: any[]              = [];
  selectedPlanKey: string         = '';
  isPlanSubmitting: boolean       = false;
  downgradeReason: string         = '';
  downgradeReasonError: string    = '';
  role: string = '';
  selectedTab: string = 'practice';
  // ─── Bank Account Details modal (Subscription Details tab) ─────────────────
  bankAccountForm!: FormGroup;
  showBankAccountModal: boolean                = false;
  bankAccountModalMode: 'add' | 'view' | 'edit' = 'add';
  isBankAccountVerifying: boolean      = false;
  isBankAccountSubmitting: boolean     = false;
  isBankAccountVerified: boolean       = false;
  isBankFieldsLocked: boolean          = false;
  bankVerifyMessage: string            = '';
  bankVerifyError: string              = '';
  bankTermsAccepted: boolean           = false;
  showAccountNumber: boolean           = false;
  showConfirmAccountNumber: boolean    = false;
  private readonly bankNamePattern = /^[A-Za-z.& ]+$/;
  private readonly accountNumberPattern = /^\d{9,18}$/;
  private readonly ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  private readonly accountHolderNamePattern = /^[A-Za-z.\s]+$/;

  // ─── Bank Account Details — dedicated GET /v3/bank-account (View/Manage flow) ──
  /** Raw record returned by GET /v3/bank-account. null = no bank account saved yet. */
  bankAccountDetails: any             = null;
  /** True once the single, one-time GET /v3/bank-account call (per Subscription
   *  Details tab load) has resolved — used to avoid flashing the wrong CTA card. */
  bankAccountFetched: boolean         = false;
  /** "Discard your changes?" confirmation, shown when closing/cancelling out of
   *  Edit Mode with unsaved changes. Reuses the existing wa-confirm-* dialog styles. */
  showBankDiscardConfirm: boolean     = false;
  private bankFormSnapshot: any       = null;

  stateList: any[]=[];
  registeredCityList: any[]=[];
  mailingCityList: any[]=[];
  
  filteredRegisteredStates: string[] = [];
  filteredMailingStates: string[] = [];

  filteredRegisteredCities: string[] = [];
  filteredMailingCities: string[] = [];

  showRegisteredStateDropdown = false;
  showMailingStateDropdown = false;

  showRegisteredCityDropdown = false;
  showMailingCityDropdown = false;

  /**
   * For Solo category view mode: always resolves the correct email to display.
   * Prefers the email from the API profile; falls back to the API-key email
   * stored in localStorage (set during login regardless of login method).
   */
  get soloEmailForDisplay(): string {
    return this.basic_profile?.email || localStorage.getItem('user_email') || 'N/A';
  }
  @ViewChild('additionalSection') additionalSection!: ElementRef;

  constructor(
    private fb: FormBuilder,
    private spinnerService: NgxSpinnerService,
    private httpservice: HttpService,
    private http: HttpClient,
    private confirmationDialogService: ConfirmationDialogService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastrService,
    private sanitizer: DomSanitizer,
    @Inject(MarketplaceVisibilityService) private marketplaceService: MarketplaceVisibilityService
  ) {}


  ngOnInit() {
    // this.route.queryParams.subscribe(params => {
    //   if (params['tab'] === 'subscription') {
    //     this.selectedTab = 'subscription';
    //     this.switchTab('subscription');
        
    //     return;
    //   }
    // });
    this.route.queryParams.subscribe(params => {
      if (params['tab'] === 'subscription') {
        this.selectedTab = 'subscription';
        this.switchTab('subscription');

        setTimeout(() => {
          const element = this.additionalSection?.nativeElement;
          if (element) {
            // Scroll the section into view
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });

            // Move a little upward after scrolling
            setTimeout(() => {
              window.scrollBy({
                top: -80,
                behavior: 'smooth'
              });
            }, 300);
          }
        }, 500);
        return;
      }
    });
    //this.getPinCode('560083')
    // "Available for Consultations" state for the unavailable-for-consultations alert.
    this.isMarketplaceListed = this.marketplaceService.isListed;
    this._marketplaceSub = this.marketplaceService.isListed$.subscribe(val => {
      this.isMarketplaceListed = val;
    });

    // Exit edit mode whenever the user navigates away (e.g. My Profile → Firm Profile).
    // This prevents stale personal-info data from leaking into the firm profile form.
    this._routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.isEditPersonalInfo = false;
        this.isEditAdditionalInfo = false;
        this.isEditProfilePic = false;
        this.isPhotoModalOpen = false;
        this.photoModalStep = 'select';
        this.modalPreviewUrl = null;
        this.submitted = false;
      }
    });

    // console.log('active tab', this.activeTab);
    // console.log('profilePictureUrl', this.profilePictureUrl);
    const category = localStorage.getItem('category');
    this.isSoloCategory = category?.toLowerCase() === 'solo';
    this.userRole = (localStorage.getItem('role') || '').trim().toUpperCase();

    // Determine which field (email or mobile) is locked based on how user logged in
    this.loginIdentifierType = localStorage.getItem('login_identifier_type') || 'email';
    this.loggedInIdentifier = this.loginIdentifierType === 'email'
      ? (localStorage.getItem('user_email') || '')
      : (localStorage.getItem('user_mobile') || '');

    this.timeOptions = this.generate12HourTimeOptions();
    this.getMatterCaseType();
    this.fetchCourtData();
    this.initializeForms();
    this.applyIdentifierFieldDisable();
    this.loadCountries();
    this.getState();
    this.setupAddressSync();
    this.filteredPracticeAreas = [...this.caseTypes];
    this.filteredServices = [];
    this.filteredLanguages = [...this.languagesList];

    // US-3: Debounced service search — 350 ms wait, cancel in-flight requests on new input
    this._serviceSearchSub = this.serviceSearch$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      switchMap((term: string) => {
        if (!term) {
          this.isLoadingServices = false;
          this.serviceSearchMode = false;
          this.servicesList = [];
          this._updateFilteredServices();
          return [];
        }
        this.isLoadingServices = true;
        return this.httpservice.sendGetRequest('/v3/services/search', { query: term });
      })
    ).subscribe({
      next: (res: any) => {
        this.isLoadingServices = false;
        let results: string[] = [];
        if (res && !res.error && res.data?.services) {
          results = res.data.services as string[];
        }
        this.servicesList = results;
        this._updateFilteredServices();
      },
      error: () => {
        this.isLoadingServices = false;
        this.servicesList = [];
        this._updateFilteredServices();
      }
    });

    // For SU: detect whether "Firm Profile" or "My Profile" submenu was clicked.
    // profileType=firm  → Firm Profile view (firm-specific labels, hide certain fields)
    // profileType=personal (or absent) → My Profile view (normal personal profile)
    //
    // IMPORTANT: getData() is called INSIDE this callback so that isFirmProfileView
    // is always resolved before getData() runs. Previously getData() was called
    // outside (after) the subscribe, so isFirmProfileView was still false when
    // profileCompletionPercentage was set, causing the personal profile's 93%
    // to show instead of the firm's 60%.
    this.route.queryParams.subscribe(params => {
      if (this.isSU && !this.isSoloCategory) {
        this.isFirmProfileView = params['profileType'] === 'firm';
        console.log('isFirmProfileView:', this.isFirmProfileView);
      } else {
        this.isFirmProfileView = false;
      }
      // Reset the Additional Information tab to the default (Practice Details)
      // whenever the Firm Profile / My Profile view changes. Without this,
      // navigating away from a tab that only exists in one view (e.g.
      // Subscription Details on Firm Profile) leaves activeTab pointing at a
      // tab that doesn't exist — and isn't rendered as a button — in the
      // other view, while its content area remains stuck on screen.
      //this.activeTab = 'practice';
        
      // if (params['tab']) {
      //   this.activeTab = params['tab'];
      // }
      this.activeTab = params['tab'] === 'subscription' ? 'subscription' : 'practice'; // Set the correct tab from query params
      // Trigger the correct profile-picture API for this view as soon as the
      // menu/route is resolved — this is the single place that fires on init.
      this._loadProfilePicForCurrentView();
      // getData() must run AFTER isFirmProfileView is set so that
      // profileCompletionPercentage reads from the correct path in the response.
      this.getData();
    });

  }

  getState() {
    this.httpservice.sendGetRequest(URLUtils.getState).subscribe(
      (res: any) => {
        this.spinnerService.hide();

        console.log('---- State API Response:', res);

        if (res.error === false) {
          this.stateList = res.data.states || [];
        } else {
          this.toast.error('Failed to load states');
        }
      },
      (error) => {
        this.spinnerService.hide();
        console.error('State API Error:', error);
      }
    );
  }
  filterStates(
  value: string,
  addressType: 'registered' | 'mailing'
): void {

  const search = (value || '').trim().toLowerCase();

  const states = this.stateList.map((state: any) =>
    typeof state === 'string'
      ? state
      : state.name || state.state || state.state_name
  );

  const filtered = search
    ? states.filter((state: string) =>
        state.toLowerCase().includes(search)
      )
    : states;

  if (addressType === 'registered') {
    this.filteredRegisteredStates = filtered;
    this.showRegisteredStateDropdown = true;
  } else {
    this.filteredMailingStates = filtered;
    this.showMailingStateDropdown = true;
  }
}
selectState(
  state: string,
  addressType: 'registered' | 'mailing'
): void {

  const addressControlName =
    addressType === 'registered'
      ? 'address'
      : 'correspondence_address';

  const addressGroup =
    this.practiceDetailsForm.get(addressControlName);

  if (!addressGroup) {
    return;
  }

  addressGroup.patchValue({
    state: state,
    city_town: ''
  });

  if (addressType === 'registered') {
    this.showRegisteredStateDropdown = false;
    this.filteredRegisteredStates = [];
    this.registeredCityList = [];
    this.filteredRegisteredCities = [];
    this.showRegisteredCityDropdown = false;
  } else {
    this.showMailingStateDropdown = false;
    this.filteredMailingStates = [];
    this.mailingCityList = [];
    this.filteredMailingCities = [];
    this.showMailingCityDropdown = false;
  }

  // Existing API call
  this.getCities(state, addressType);
}
filterCities(
  value: string,
  addressType: 'registered' | 'mailing'
): void {

  const search = (value || '').trim().toLowerCase();

  const cities =
    addressType === 'registered'
      ? this.registeredCityList
      : this.mailingCityList;

  const cityNames = cities.map((city: any) =>
    typeof city === 'string'
      ? city
      : city.name || city.city || city.city_name
  );

  const filtered = search
    ? cityNames.filter((city: string) =>
        city.toLowerCase().includes(search)
      )
    : cityNames;

  if (addressType === 'registered') {
    this.filteredRegisteredCities = filtered;
    this.showRegisteredCityDropdown = true;
  } else {
    this.filteredMailingCities = filtered;
    this.showMailingCityDropdown = true;
  }
}
selectCity(
  city: string,
  addressType: 'registered' | 'mailing'
): void {

  const addressControlName =
    addressType === 'registered'
      ? 'address'
      : 'correspondence_address';

  this.practiceDetailsForm
    .get(addressControlName)
    ?.get('city_town')
    ?.setValue(city);

  if (addressType === 'registered') {
    this.showRegisteredCityDropdown = false;
  } else {
    this.showMailingCityDropdown = false;
  }
}
  getCities(
    stateName: string,
    addressType: 'registered' | 'mailing',
    selectedCity: string = ''
  ): void {

    if (!stateName) {
      return;
    }

    this.spinnerService.show();

    const url = URLUtils.getCities(stateName);

    this.httpservice.sendGetRequest(url).subscribe(
      (res: any) => {

        this.spinnerService.hide();

        console.log('---- City API Response:', res);

        if (res.error === false) {

          const cities = res.data.cities || res;

          if (addressType === 'registered') {

            this.registeredCityList = cities;

            if (selectedCity) {
              this.practiceDetailsForm
                .get('address')
                ?.get('city_town')
                ?.setValue(selectedCity);
            }

          } else {

            this.mailingCityList = cities;

            if (selectedCity) {
              this.practiceDetailsForm
                .get('correspondence_address')
                ?.get('city_town')
                ?.setValue(selectedCity);
            }
          }

        } else {

          this.toast.error('Failed to load cities');
        }
      },
      (error) => {

        this.spinnerService.hide();

        console.error('City API Error:', error);
      }
    );
  }
  onStateChange(addressType: 'registered' | 'mailing'): void {

    const addressControlName =
      addressType === 'registered'
        ? 'address'
        : 'correspondence_address';

    const addressGroup =
      this.practiceDetailsForm.get(addressControlName);

    if (!addressGroup) {
      return;
    }

    const state = addressGroup.get('state')?.value;

    // Clear city when state changes
    addressGroup.patchValue({
      city_town: ''
    }, {
      emitEvent: false
    });

    if (!state) {

      if (addressType === 'registered') {
        this.registeredCityList = [];
      } else {
        this.mailingCityList = [];
      }

      return;
    }

    // Get cities based on selected state
    this.getCities(state, addressType);
  }

  getBioSummary() {
    const formValue = this.personalInfoForm.getRawValue();
    const practiceValue = this.practiceDetailsForm.getRawValue();
    const isFirmContext = this.isFirmProfileView || this.isAAM;

    const payload = {
      name: isFirmContext
        ? (formValue.contact_person || this.profile_details?.name || '')
        : (formValue.contact_person || this.profile_details?.name || ''),

      gender: formValue.gender || this.profile_details?.gender || '',

      city: this.profile_details?.firm?.address?.city_town
        || this.profile_details?.court_enrollments?.[0]?.city
        || '',

      // practice_areas: practiceValue.practice_areas?.length
      //   ? practiceValue.practice_areas
      //   : (this.profile_details?.practice_areas || []),

      // services_offered: practiceValue.services_offered?.length
      //   ? practiceValue.services_offered
      //   : (this.profile_details?.services_offered || []),
            
      practice_areas: practiceValue.practice_areas ?? [],
      services_offered: practiceValue.services_offered ?? [],

      years_of_experience: (practiceValue.years_of_experience !== null && practiceValue.years_of_experience !== undefined)
        ? practiceValue.years_of_experience
        : (this.profile_details?.years_of_experience ?? null),

      // languages_spoken: practiceValue.languages_spoken?.length
      //   ? practiceValue.languages_spoken
      //   : (this.profile_details?.languages_spoken || []),

      // cases_handled: practiceValue.cases_handled?.length
      //   ? practiceValue.cases_handled
      //   : (this.profile_details?.cases_handled || []),
      languages_spoken: practiceValue.languages_spoken ?? [],
      cases_handled: practiceValue.cases_handled ?? [],

      education: (this.education.value?.length
        ? this.education.value
        : (this.profile_details?.education || [])
      ).map((edu: any) => ({
        degree: edu.degree,
        university: edu.university,
        passing_year: edu.passing_year
      })),

      awards: (
        isFirmContext
          ? (this.awards.value?.length ? this.awards.value : (this.firm_details?.awards || []))
          : (this.awards.value?.length ? this.awards.value : (this.profile_details?.awards || []))
      ).map((award: any) => ({
        award_name: award.award_name,
        purpose: award.purpose,
        year_of_award: award.year_of_award
      })),

      court_enrollments: (this.courtEnrollments.value?.length
        ? this.courtEnrollments.value
        : (this.profile_details?.court_enrollments || [])
      ).map((court: any) => ({
        court_name: court.court_type || court.court_name,
        court_type: court.court_type || court.court_name,
        state: court.state,
        city: court.city
      }))
    };

    this.isBioLoading = true;

    this.httpservice.sendPostRequest(URLUtils.getBioSummary, payload).subscribe({
      next: (res: any) => {
        this.isBioLoading = false;
        if (!res.error) {
          let bioText = res.data?.bio || '';
          this.personalInfoForm.patchValue({ bio_description: bioText });
          this.basic_profile.bio_description = bioText;
        }
      },
      error: (err: any) => {
        this.isBioLoading = false;
        if (err?.error?.error === true && Array.isArray(err?.error?.errors)) {
          const fieldNames = err.error.errors
            .map((e: any) => e.field
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c: string) => c.toUpperCase())
            )
            .join(', ');
          this.toast.error(
            `Please fill in the necessary fields: ${fieldNames} for the bio summarization`,
            '',
            { timeOut: 5000 }
          );
          return;
        }
        this.toast.error('Check your connection and try again');
      }
    });
  }

  ngOnDestroy() {
    // Unsubscribe from router events to prevent memory leaks
    this._routerSub?.unsubscribe();
    this._serviceSearchSub?.unsubscribe();
    this._marketplaceSub?.unsubscribe();
  }

  /**
   * Called from the "Available for Consultations" link in the
   * unavailable-for-consultations alert. Scrolls to and highlights
   * the top-bar toggle instead of navigating anywhere.
   */
  scrollToMarketplaceToggle(): void {
    this.marketplaceService.requestToggleHighlight();
  }
   getMatterCaseType() {
      this.spinnerService.show();
      this.httpservice.sendGetRequest(URLUtils.checkCaseType).subscribe((res: any) => {
        this.spinnerService.hide();
        this.caseTypes = res.data || [];
        this.filteredPracticeAreas = [...this.caseTypes];
      }); // get case types
    }

  // ── Court Practice Details helpers ──────────────────────────────────────

  /** Fetch court states/cities from API and suggested court types based on practice areas */
  fetchCourtData(): void {
    // Fetch states and cities
    this.httpservice.sendGetRequest('/v3/court/states').subscribe(
      (res: any) => {
        if (res && !res.error && Array.isArray(res.data?.states)) {
          this.courtStatesData = res.data.states.map((s: any) => ({
            state: s.state,
            cities: Array.isArray(s.cities) ? s.cities : []
          }));
        }
      },
      () => { /* silently ignore */ }
    );
    // Fetch suggested court types based on practice areas
    this.fetchCourtSuggestions();
  }

  /** Fetch court type suggestions based on current practice areas */
  fetchCourtSuggestions(): void {
    const practiceAreas = this.practiceDetailsForm?.get('practice_areas')?.value || [];
    // When no practice areas are selected, fetch with empty array to get all_courts from API.
    // If we already have all_courts cached, use them directly without a network call.
    if (!practiceAreas.length) {
      if (this.courtAllPredefinedTypes.length) {
        this.isLoadingCourtTypes = false;
        this.courtSuggestedTypes = [...this.courtAllPredefinedTypes];
        this.hasSuggestedCourtTypes = false;
        return;
      }
      // Fall through to API call with empty practice_areas to fetch all_courts
    }
    const body = { practice_areas: practiceAreas };
    this.isLoadingCourtTypes = true;
    this.httpservice.sendPostRequest('/v3/court/suggest', body).subscribe(
      (res: any) => {
        this.isLoadingCourtTypes = false;
        if (res && !res.error && res.data) {
          // Store all_courts for "View All Courts" expanded state
          if (Array.isArray(res.data.all_courts) && res.data.all_courts.length) {
            this.courtAllPredefinedTypes = res.data.all_courts as string[];
          }
          if (Array.isArray(res.data.court_suggestions) && res.data.court_suggestions.length) {
            // Build list: Supreme Court of India always first, then API suggestions (deduped)
            const suggestions: string[] = res.data.court_suggestions;
            const withoutSupreme = suggestions.filter(
              (s: string) => s.toLowerCase() !== 'supreme court of india'
            );
            this.courtSuggestedTypes = ['Supreme Court of India', ...withoutSupreme];
            this.hasSuggestedCourtTypes = true;
          } else {
            this.courtSuggestedTypes = [...this.courtAllPredefinedTypes];
            this.hasSuggestedCourtTypes = false;
          }
        } else {
          this.courtSuggestedTypes = [...this.courtAllPredefinedTypes];
          this.hasSuggestedCourtTypes = false;
        }
      },
      () => {
        this.isLoadingCourtTypes = false;
        this.courtSuggestedTypes = [...this.courtAllPredefinedTypes];
        this.hasSuggestedCourtTypes = false;
      }
    );
  }

  /** Accessor for the court_enrollments FormArray */
  get courtEnrollments(): FormArray {
    return this.practiceDetailsForm.get('court_enrollments') as FormArray;
  }

  /** Build a single court-enrollment FormGroup */
  private _buildCourtRow(data?: any): FormGroup {
    return this.fb.group({
      court_type: [data?.court_type || '', Validators.required],
      state:      [data?.state || ''],
      city:       [data?.city || '']
    });
  }

  /** Validate all court rows before save — returns error message or null */
  validateCourtEnrollments(): string | null {
    for (let i = 0; i < this.courtEnrollments.length; i++) {
      const row = this.courtEnrollments.at(i) as FormGroup;
      const courtType = row.get('court_type')?.value;
      const state = (row.get('state')?.value || '').trim();
      const city = (row.get('city')?.value || '').trim();

      if (!courtType) return `Court Type is required.`;
      if (!state) return `State is required.`;
      if (!city) return `City is required.`;
    }
    return null;
  }

  /** Add a blank court enrollment row */
  addCourtEnrollment(): void {
    const i = this.courtEnrollments.length;
    this.courtEnrollments.push(this._buildCourtRow());
    this.courtTypeSearch[i] = '';
    this.courtStateSearch[i] = '';
    this.courtCitySearch[i] = '';
    this.showCityOtherInput[i] = false;
    this.courtCityOtherInput[i] = '';
  }

  /** Remove a court enrollment row */
  removeCourtEnrollment(i: number): void {
    this.courtEnrollments.removeAt(i);
    this.courtTypeSearch.splice(i, 1);
    this.courtStateSearch.splice(i, 1);
    this.courtCitySearch.splice(i, 1);
    this.showCityOtherInput.splice(i, 1);
    this.courtCityOtherInput.splice(i, 1);
  }

  /** Called when Court Type changes — reset dependent fields */
  onCourtTypeChange(i: number): void {
    const row = this.courtEnrollments.at(i) as FormGroup;
    const courtType = (row.get('court_type')?.value || '').toLowerCase();

    if (courtType === 'supreme court' || courtType === 'supreme court of india') {
      // Supreme Court is always located in Delhi / New Delhi
      row.patchValue({ state: 'Delhi', city: 'New Delhi' });
    } else {
      row.patchValue({ state: '', city: '' });
    }

    this.showCityOtherInput[i] = false;
    this.courtCityOtherInput[i] = '';
    // Re-fetch suggestions whenever practice areas may be relevant
    this.fetchCourtSuggestions();
  }
  

  /** Called when State changes — reset city */
  onCourtStateChange(i: number): void {
    const row = this.courtEnrollments.at(i) as FormGroup;
    row.patchValue({ city: '' });
    this.showCityOtherInput[i] = false;
    this.courtCityOtherInput[i] = '';
  }

  /** Called when City changes */
  onCourtCityChange(i: number): void {
    const row = this.courtEnrollments.at(i) as FormGroup;
    const city = row.get('city')?.value;
    if (city === '__other__') {
      this.showCityOtherInput[i] = true;
      row.patchValue({ city: '' });
    }
  }

  /** Get list of states from the court/states API */
  getCourtStates(i: number): string[] {
    return this.courtStatesData.map(s => s.state).sort();
  }

  /** Get list of cities for the selected state from the court/states API */
  getCourtCities(i: number): string[] {
    const row = this.courtEnrollments.at(i) as FormGroup;
    const state = row.get('state')?.value;
    if (!state) return [];
    const stateData = this.courtStatesData.find(s => s.state === state);
    return stateData ? [...stateData.cities].sort() : [];
  }

  /** Whether a court row field should show validation error */
  isCourtFieldInvalid(i: number, field: string): boolean {
    const ctrl = (this.courtEnrollments.at(i) as FormGroup).get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.touched || this.submitted));
  }

  /** Get filtered court types for search */
  // getFilteredCourtTypes(i: number): string[] {
  //   const q = (this.courtTypeSearch[i] || '').toLowerCase();
  //   // In search mode: always search across the full list
  //   if (q) {
  //     const pool = this.courtAllPredefinedTypes.length ? this.courtAllPredefinedTypes : this.courtSuggestedTypes;
  //     return pool.filter(t => t.toLowerCase().includes(q));
  //   }
  //   // Expanded: show full list
  //   if (this.showAllCourtTypes === i) {
  //     return [...this.courtAllPredefinedTypes];
  //   }
  //   // Default: show suggested types only
  //   return [...this.courtSuggestedTypes];
  // }
  getFilteredCourtTypes(i: number): string[] {
    const q = (this.courtTypeSearch[i] || '').toLowerCase();

    let courts =
      this.showAllCourtTypes === i
        ? [...this.courtAllPredefinedTypes]
        : [...this.courtSuggestedTypes];

    // Remove Supreme Court if it already exists in another row
    const supremeExists = this.courtEnrollments.controls.some((control, index) =>
      index !== i &&
      ['supreme court', 'supreme court of india'].includes(
        (control.get('court_type')?.value || '').toLowerCase()
      )
    );

    if (supremeExists) {
      courts = courts.filter(
        c =>
          !['supreme court', 'supreme court of india'].includes(c.toLowerCase())
      );
    }

    if (q) {
      courts = courts.filter(c => c.toLowerCase().includes(q));
    }

    return courts;
  }

  /** Display label for a court_type key */
  // getCourtTypeLabel(courtType: string): string {
  //   const map: Record<string, string> = {
  //     district_court: 'District & Other Lower Courts',
  //     high_court: 'High Court',
  //     supreme_court: 'Supreme Court'
  //   };
  //   return map[courtType] || courtType;
  // }
  /** Display label for a court_type value (may be raw string from API) */
  getCourtTypeLabel(courtType: string): string {
    if (!courtType) return 'Select Court Type';
    return courtType;
  }

  /** Add a "Types of Cases Handled" chip from keyboard input */
  addCaseHandledFromInput(event: Event): void {
    event.preventDefault();
    const raw = (this.caseHandledInput || '').trim();
    if (!raw) return;
    if (raw.length > 500) return;
    const current: string[] = this.practiceDetailsForm.get('cases_handled')?.value || [];

    if (this.editingCaseIndex >= 0) {
      // Edit mode: replace the tag at editingCaseIndex
      // Duplicate check: allow if the only match is the tag being edited
      const isDuplicate = current.some((c, idx) => c === raw && idx !== this.editingCaseIndex);
      if (isDuplicate) { return; }
      const updated = [...current];
      updated[this.editingCaseIndex] = raw;
      this.practiceDetailsForm.get('cases_handled')?.setValue(updated);
      this.editingCaseIndex = -1;
    } else {
      // Add mode: prevent duplicates
      if (current.includes(raw)) return;
      this.practiceDetailsForm.get('cases_handled')?.setValue([...current, raw]);
    }
    this.caseHandledInput = '';
  }

  /** Begin editing a "Types of Cases Handled" tag — populate input and remove from list temporarily */
  editCaseHandled(chip: string, index: number): void {
    this.caseHandledInput = chip;
    this.editingCaseIndex = index;
    // Focus the input after Angular updates the DOM
    setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>('#field-cases_handled .chip-inline-input');
      if (el) { el.focus(); }
    }, 0);
  }

  /** Remove a "Types of Cases Handled" chip */
  removeCaseHandled(chip: string): void {
    const current: string[] = this.practiceDetailsForm.get('cases_handled')?.value || [];
    this.practiceDetailsForm.get('cases_handled')?.setValue(current.filter((c: string) => c !== chip));
  }

  applyIdentifierFieldDisable() {
    if (this.loginIdentifierType === 'email' || this.loginIdentifierType === 'mobile') {
      this.personalInfoForm.get('email')?.disable();
      this.personalInfoForm.get('mobile')?.disable();
    } 
    // else {
    //   this.personalInfoForm.get('mobile')?.disable();
    //   this.personalInfoForm.get('email')?.enable();
    // }
  }

  // getPinCode(pincode:any){
  //    this.httpservice.sendGetRequest(URLUtils.getPinCodeDetails(pincode)).subscribe(
  //     (res: any) => {
  //       this.spinnerService.hide();
  //       console.log('----API Response:', res); // Debug log
        
  //       if (res.error === false ) {
  //         // Extract the profile from the nested structure
  //         console.log(res)
      
       
  //       } else {
  //         this.toast.error('Failed to load profile data');
  //       }
  //     },
  //     (error) => {
   
  //     }
  //   );
  // }

  getPinCode(
    pincode: string,
    addressType: 'registered' | 'mailing'
  ): void {

    this.spinnerService.show();

    this.httpservice
      .sendGetRequest(URLUtils.getPinCodeDetails(pincode))
      .subscribe(
        (res: any) => {

          this.spinnerService.hide();

          console.log('---- Pincode API Response:', res);

          if (res.error === false) {

            const addressControlName =
              addressType === 'registered'
                ? 'address'
                : 'correspondence_address';

            const addressGroup =
              this.practiceDetailsForm.get(addressControlName);

            if (!addressGroup) {
              return;
            }

            // Change these according to your actual API response
            const city = res.city || res.data?.city || '';
            const state = res.state || res.data?.state || '';

            console.log('City:', city);
            console.log('State:', state);

            // Fill City & State
            addressGroup.patchValue({
              city_town: city,
              state: state
            }, {
              emitEvent: true
            });

          } else {

            const addressControlName =
              addressType === 'registered'
                ? 'address'
                : 'correspondence_address';

            const addressGroup =
              this.practiceDetailsForm.get(addressControlName);

            addressGroup?.patchValue({
              city_town: '',
              state: ''
            }, {
              emitEvent: true
            });

            this.toast.error('Invalid pincode');
          }
        },
        (error) => {

          this.spinnerService.hide();

          console.error('Pincode API Error:', error);

          const addressControlName =
            addressType === 'registered'
              ? 'address'
              : 'correspondence_address';

          const addressGroup =
            this.practiceDetailsForm.get(addressControlName);

          addressGroup?.patchValue({
            city_town: '',
            state: ''
          }, {
            emitEvent: true
          });

          this.toast.error(
            'Unable to get city and state for this pincode'
          );
        }
      );
  }

  onPincodeInput(addressType: 'registered' | 'mailing'): void {

    if (this.isTMorGH) {
      return;
    }

    const addressControlName =
      addressType === 'registered'
        ? 'address'
        : 'correspondence_address';

    const addressGroup = this.practiceDetailsForm.get(addressControlName);

    if (!addressGroup) {
      return;
    }

    const pincode = addressGroup.get('zipcode')?.value;

    // Remove anything other than numbers
    const cleanPincode = String(pincode || '').replace(/\D/g, '');

    // Keep only 6 digits
    if (cleanPincode.length > 6) {
      addressGroup.get('zipcode')?.setValue(cleanPincode.substring(0, 6), {
        emitEvent: false
      });
      return;
    }

    // If pincode is not 6 digits, clear city and state
    if (cleanPincode.length !== 6) {
      addressGroup.get('city_town')?.setValue('', {
        emitEvent: false
      });

      addressGroup.get('state')?.setValue('', {
        emitEvent: false
      });

      return;
    }

    // Exactly 6 digits -> call API
    this.getPinCode(cleanPincode, addressType);
  }
  onPincodeEnter(addressType: 'registered' | 'mailing'): void {

    if (this.isTMorGH) {
      return;
    }

    const addressControlName =
      addressType === 'registered'
        ? 'address'
        : 'correspondence_address';

    const addressGroup = this.practiceDetailsForm.get(addressControlName);

    if (!addressGroup) {
      return;
    }

    const pincode = String(
      addressGroup.get('zipcode')?.value || ''
    ).replace(/\D/g, '');

    if (pincode.length === 6) {
      this.getPinCode(pincode, addressType);
    }
  }

  // Initialize all forms
  initializeForms() {
    // Personal Information form (combines firm and personal details)
     const firmNameValidators = this.isSoloCategory ? [] : [Validators.required];
     const genderValidators = this.isSoloCategory ? [Validators.required] : [];
    
    this.personalInfoForm = this.fb.group({
      fullname: ['', firmNameValidators],
      country: ['', Validators.required],
      contact_person: ['', Validators.required],
      nationality: ['', Validators.required],
      email: ['', [Validators.required, Validators.pattern(this.emailPattern)]],
      billing_currency: ['', Validators.required],
      mobile: ['', [Validators.required, Validators.pattern(this.phonePattern)]],
      date_of_birth: [''],
      website: ['', Validators.pattern(this.websitePattern)],
      gender: [''],
      bio_description: [''],
      consultation_fee: ['', [Validators.required]]
    });

    // Practice details form
    this.practiceDetailsForm = this.fb.group({
  bar_council_id: [''],
  years_of_experience: [null],
  practice_areas: [[], [Validators.required, (control: any) => control.value?.length ? null : { required: true }]],
  services_offered: [[]],
  languages_spoken: [[]],
  cases_handled: [[]],
  court_enrollments: this.fb.array([]),

  address: this.fb.group({
    house_flat_no: ['', [Validators.required]],
    street: ['', Validators.maxLength(100)],
    city_town: ['', [Validators.required, Validators.maxLength(50)]],
    state: ['', [Validators.required, Validators.maxLength(50)]],
    country: [''],
    zipcode: ['', [
      Validators.required,
      Validators.pattern(`${this.zipcode5Pattern.source}|${this.zipcode6Pattern.source}`)
    ]]
  }),

  correspondence_address: this.fb.group({
    house_flat_no: ['', [Validators.required]],
    street: ['', Validators.maxLength(100)],
    city_town: ['', [Validators.required, Validators.maxLength(50)]],
    state: ['', [Validators.required, Validators.maxLength(50)]],
    country: [''],
    zipcode: ['', [
      Validators.required,
      Validators.pattern(`${this.zipcode5Pattern.source}|${this.zipcode6Pattern.source}`)
    ]]
  }),

  /** ✅ ADD THIS */
  sameAsRegistered: [false]
});

    // Education form with arrays
    this.educationForm = this.fb.group({
      education: this.fb.array([]),
      certifications: this.fb.array([]),
      awards: this.fb.array([])
    });

    // Bank account details form (Subscription Details tab)
    this.initBankAccountForm();

    // Availability form
    this.availabilityForm = this.fb.group({
      weekly_schedule: this.fb.array([]),
      timezone: ['Asia/Kolkata', Validators.required],
      // slot_duration: [30, [Validators.min(15), Validators.max(120)]],
      // buffer_time: [0, [Validators.min(0), Validators.max(60)]],
      // advance_booking_window_hours: [4, [Validators.min(1), Validators.max(720)]],
      // cancellation_window_hours: [2, [Validators.min(1), Validators.max(168)]],
      // reschedule_window_hours: [2, [Validators.min(1), Validators.max(168)]]
    });

    // Initialize weekly schedule with all days
    this.initializeWeeklySchedule();
  }
//   private setupAddressSync(): void {
//   const sameCtrl = this.practiceDetailsForm.get('sameAsRegistered');
//   const regAddr = this.practiceDetailsForm.get('address') as FormGroup;
//   const mailAddr = this.practiceDetailsForm.get('correspondence_address') as FormGroup;

//   if (!sameCtrl || !regAddr || !mailAddr) return;

//   sameCtrl.valueChanges.subscribe((checked: boolean) => {
//     if (checked) {
//       mailAddr.patchValue(regAddr.getRawValue(), { emitEvent: false });
//       mailAddr.disable({ emitEvent: false });
//     } else {
//       mailAddr.enable({ emitEvent: false });
//     }
//   });

//   regAddr.valueChanges.subscribe(value => {
//     if (sameCtrl.value) {
//       mailAddr.patchValue(value, { emitEvent: false });
//     }
//   });
// }
private setupAddressSync(): void {

  const sameCtrl = this.practiceDetailsForm.get('sameAsRegistered');
  const regAddr = this.practiceDetailsForm.get('address') as FormGroup;
  const mailAddr = this.practiceDetailsForm.get('correspondence_address') as FormGroup;

  if (!sameCtrl || !regAddr || !mailAddr) {
    return;
  }

  // If Registered Address changes while checkbox is checked,
  // automatically update Mailing Address.
  regAddr.valueChanges.subscribe(value => {

    if (sameCtrl.value === true) {
      mailAddr.patchValue(value, {
        emitEvent: false
      });
    }

  });
}
onSameAsRegisteredChange(event: Event): void {

  const checked = (event.target as HTMLInputElement).checked;

  const regAddr = this.practiceDetailsForm.get('address') as FormGroup;
  const mailAddr = this.practiceDetailsForm.get('correspondence_address') as FormGroup;

  if (!regAddr || !mailAddr) {
    return;
  }

  if (checked) {

    // Copy Registered Address → Mailing Address
    mailAddr.patchValue(
      regAddr.getRawValue(),
      {
        emitEvent: false
      }
    );

    // Load cities for the copied state
    const state = regAddr.get('state')?.value;

    if (state) {
      this.getCities(state, 'mailing');
    }

    // Disable Mailing Address fields
    mailAddr.disable({
      emitEvent: false
    });

  } else {

    // Enable Mailing Address again
    mailAddr.enable({
      emitEvent: false
    });

  }
}

  editPersonalInfo() {
    // if (!ensureActiveSubscription(this.confirmationDialogService)) return;
    this.isEditPersonalInfo = true;
    // For firm profile context (AAM or SU Firm Profile), consultation_fee is hidden
    // and must not block form validity — clear the required validator.
    // For all other contexts (including SU My Profile), restore it.
    const feeCtrl = this.personalInfoForm.get('consultation_fee');
    if (feeCtrl) {
      if (this.isFirmProfileView || this.isAAM) {
        feeCtrl.clearValidators();
      } else {
        feeCtrl.setValidators([Validators.required]);
      }
      feeCtrl.updateValueAndValidity();
    }
    // Re-apply the locked field after the form becomes visible in edit mode
    setTimeout(() => this.applyIdentifierFieldDisable(), 0);
  }

  editAdditionalInfo() {
    // if (!ensureActiveSubscription(this.confirmationDialogService)) return;
    this.isEditAdditionalInfo = true;
  }


  switchTab(tab: string): void {

    this.activeTab = tab;

    // GET /v3/bank-account is called exactly once per Subscription Details
    // tab load — this determines whether the "Add" or "View / Manage" CTA
    // card is shown, independent of the wider profile payload.
    if (tab === 'settlement' && !this.bankAccountFetched) {
      this.loadBankAccountDetails();
    }
    // this.submitted = false;
  }

  editProfilePicture() {
    // if (!ensureActiveSubscription(this.confirmationDialogService)) return;
    this.isEditProfilePic = true;
    this.photoModalStep = 'select';

    if (this.profilePictureUrl) {
      // Existing photo — open the modal so the user can preview it, upload a
      // replacement, or remove it.
      this.isPhotoModalOpen = true;
    } else {
      // No photo yet. Jumping straight to the OS file picker here (e.g. from
      // the "Complete Now" banner) catches the user off guard — a native
      // dialog pops up with no on-screen context for why. Instead, guide
      // them to the avatar itself: scroll it into view, highlight it briefly,
      // and let them open the file picker with their own click.
      this.promptAddProfilePhoto();
    }
  }

  // Scrolls the avatar into view and shows a brief inline hint bubble + pulse
  // so the user notices where to click, instead of a file dialog appearing
  // with no visible cause. The hint disappears the moment they click the
  // avatar (see onAvatarClick), so it never adds an extra click of its own.
  promptAddProfilePhoto() {
    const avatarEl = document.getElementById('profileAvatarWrap');
    avatarEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.highlightAvatar = true;
    this.showAvatarHint = true;
    setTimeout(() => {
      this.highlightAvatar = false;
      this.showAvatarHint = false;
    }, 3000);
  }

  // ── WhatsApp-style avatar dropdown ────────────────────────────────────────────
  onAvatarClick(event?: Event) {
    // if (!ensureActiveSubscription(this.confirmationDialogService)) return;
    event?.stopPropagation();
    this.showAvatarHint = false;

    if (!this.profilePictureUrl) {
      // No photo yet — "Upload New Photo" is the only option anyway, so open
      // the file picker straight away instead of making the user open a
      // one-item menu first.
      this.triggerFileInput();
      return;
    }

    this.showAvatarMenu = !this.showAvatarMenu;
  }

  toggleAvatarMenu(event?: Event) {
    // if (!ensureActiveSubscription(this.confirmationDialogService)) return;
    event?.stopPropagation();
    this.showAvatarMenu = !this.showAvatarMenu;
  }

  closeAvatarMenu() {
    this.showAvatarMenu = false;
  }

  onUploadNewPhotoClick() {
    this.closeAvatarMenu();
    this.triggerFileInput();
  }

  onRemovePhotoClick() {
    this.closeAvatarMenu();
    this.showRemoveConfirm = true;
  }

  cancelRemovePhoto() {
    this.showRemoveConfirm = false;
  }

  confirmRemovePhoto() {
    this.showRemoveConfirm = false;
    this.removeProfilePicture();
  }
   
   cancelPersonalInfo() {
    this.isEditPersonalInfo = false;
    this.submitted = false;
    this.resetBioExpansion();
    // Reset form to original values by reloading data
    this.loadFormData();
    this.getData();
  }

  cancelAdditionalInfo() {
    this.isEditAdditionalInfo = false;
    this.submitted = false;
    // Reset form to saved values (including court_enrollments, cases_handled, etc.)
    this.populateAllForms();
    // Reset practice-tab UI state so no stale inputs or open dropdowns remain
    this.showPracticeAreaDropdown = false;
    this.practiceAreaSearch = '';
    this.showServiceDropdown = false;
    this.serviceInput = '';
    this.serviceSearchMode = false;
    this.showOtherServiceInput = false;
    this.otherServiceValue = '';
    this.otherServiceSuggestions = [];
    this.duplicateServiceMatch = '';
    this.showAllServices = false;
    this.languageInput = '';
    this.caseHandledInput = '';
    this.editingCaseIndex = -1;
  }

  cancelProfilePic() {
    this.closePhotoModal();
  }

  // ── Close the Set Profile Photo modal entirely (Cancel / X) ──────────────────
  closePhotoModal() {
    this.isEditProfilePic = false;
    this.isPhotoModalOpen = false;
    this.photoModalStep = 'select';
    this.isAdjustingPic = false;
    this._pendingUploadFile = null;
    this.modalPreviewUrl = null;
    this.resetAdjust();

    const fileInput = document.getElementById('profilePictureInput') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }

  // ── Move from the photo-selection screen to the crop/reposition screen ───────
  continueToCrop() {
    if (!this._pendingUploadFile) return;
    this.startAdjust();
    this.photoModalStep = 'crop';
  }

  // ── Return from crop/reposition screen back to the photo-selection screen ────
  backToSelect() {
    this.photoModalStep = 'select';
  }

  // ── Whether a new photo has been selected and is awaiting crop/save ──────────
  get hasPendingPhoto(): boolean {
    return !!this._pendingUploadFile;
  }

  private _buildPersonalInfoPayload(): any | null {
    const current = this.personalInfoForm.getRawValue();
    const saved   = this._savedPersonalInfo;

    // Helper: true if a scalar value has changed
    const changed = (key: string) =>
      !saved || JSON.stringify(current[key]) !== JSON.stringify(saved[key]);

    const payload: any = {};
    const firmPayload: any = {};

    // ── Top-level "name" (mapped from form control "contact_person") ──────────
    if (changed('contact_person')) {
      if (this.isFirmProfileView || this.isAAM) {
        firmPayload.contact_person = current.contact_person;
      } else {
        payload.name = current.contact_person;
      }
    }

    // ── mobile / contact_phone ─────────────────────────────────────────────────
    if (changed('mobile')) {
      if (this.isFirmProfileView || this.isAAM) {
        firmPayload.contact_phone = current.mobile;
      } else {
        payload.mobile = current.mobile;
      }
    }

    // ── nationality ───────────────────────────────────────────────────────────
    if (changed('nationality')) {
      payload.nationality = current.nationality || 'Indian';
    }

    // ── bio_description / firm_description ───────────────────────────────────────────────────────
    if (changed('bio_description')) {
      if (this.isFirmProfileView || this.isAAM) {
        firmPayload.firm_description = current.bio_description;
      } else {
        payload.bio_description = current.bio_description;
      }
    }

    // ── consultation_fee (form scalar → API object) ───────────────────────────
    if (changed('consultation_fee')) {
      payload.consultation_fee = current.consultation_fee
        ? {
            amount: current.consultation_fee,
            symbol: current.billing_currency === 'IndianRupee(INR)' ? '₹' : ''
          }
        : null;
    }

    // ── billing_currency (firm-level) ─────────────────────────────────────────
    if (changed('billing_currency')) {
      firmPayload.billing_currency = current.billing_currency || 'IndianRupee(INR)';
      // If consultation_fee is already in payload, update its symbol too
      if (payload.consultation_fee) {
        payload.consultation_fee.symbol =
          current.billing_currency === 'IndianRupee(INR)' ? '₹' : '';
      }
    }

    // ── website (firm-level) ──────────────────────────────────────────────────
    if (changed('website')) {
      firmPayload.website = current.website;
    }

    // ── email: only when logged in via mobile (email is editable) ─────────────
    if (this.loginIdentifierType === 'mobile' && changed('email')) {
      payload.email = current.email;
      firmPayload.email = current.email;
    }

    // ── gender (applies to solo and firm accounts: SU, TM, GH) ────────────────
    if (changed('gender')) {
      payload.gender = current.gender;
    }

    // ── Solo-specific fields ──────────────────────────────────────────────────
    if (this.isSoloCategory) {
      if (changed('date_of_birth')) {
        payload.date_of_birth = current.date_of_birth
          ? this.formatDateToYMD(current.date_of_birth)
          : null;
      }
      // For solo category, the name field must be sent to both top-level "name"
      // and firm.fullname so both are kept in sync on the backend.
      if (changed('contact_person')) {
        firmPayload.fullname = current.contact_person;
      }
    } else {
      // ── Entity-specific: firm.fullname ──────────────────────────────────────
      if (changed('fullname')) {
        firmPayload.fullname = current.fullname;
      }
    }

    if (Object.keys(firmPayload).length > 0) {
      payload.firm = firmPayload;
    }

    // Nothing changed at all
    if (Object.keys(payload).length === 0) return null;

    return payload;
  }

   savePersonalInfo() {
    console.log('savePersonalInfo called');
    console.log('personalInfoForm value:', this.personalInfoForm);

    // if (!ensureActiveSubscription(this.confirmationDialogService)) return;
    this.submitted = true;

    if (this.personalInfoForm.invalid) {
      this.scrollToFirstInvalid();
      return;
    }

    //  const bio = this.personalInfoForm.get('bio_description')?.value || '';
    //  const wordCount = bio.trim().split(/\s+/).filter((word: string) => word.length > 0).length;
    //  if (wordCount > 500) {
    //    this.toast.error(
    //      `Bio cannot exceed 500 words. Current count: ${wordCount} words.`
    //    );
    //    return;
    //  } // show alert if exceeds 500


    if (!this.personalInfoForm.valid) {
      this.toast.error('Please fill all required fields in Personal Information');
      return;
    }

    // Build diff-only payload — only changed fields are sent
    const payload = this._buildPersonalInfoPayload();

    if (!payload) {
      // Nothing changed — just close edit mode silently
      this.isEditPersonalInfo = false;
      this.submitted = false;
      return;
    }

    this.spinnerService.show();
    this.httpservice.sendPatchRequest(URLUtils.extendedProfile, payload).subscribe(
      (res: any) => {
        this.spinnerService.hide();
        if (res.error === false) {
          this.toast.success(res.msg || 'Personal information updated successfully');
          this.isEditPersonalInfo = false;
          this.submitted = false;
          // Update dashboard header name / firm name only if they were changed
          const currentValue = this.personalInfoForm.getRawValue();
          if (payload.name) {
            this.httpservice.setName(currentValue.contact_person);
          }
          if (payload.firm?.fullname && !this.isSoloCategory) {
            this.httpservice.setFirmName(currentValue.fullname);
          }
          this.getData();
        } else {
          this.toast.error(res.msg);
        }
      },
      (error: HttpErrorResponse) => {
        this.spinnerService.hide();
        this.handleApiErrors(error);
      }
    );
  }

  private _captureSnapshots(): void {
    this._savedPersonalInfo = this.personalInfoForm.getRawValue();
    const pd = this.practiceDetailsForm.getRawValue();
    this._savedPracticeDetails = {
      ...pd,
      court_enrollments: this.courtEnrollments.value
    };
    this._savedEducation = {
      education: this.education.value,
      certifications: this.certifications.value,
      awards: this.awards.value
    };
    const avail = this.availabilityForm.value;
    this._savedAvailability = {
      ...avail,
      weekly_schedule: (avail.weekly_schedule || []).filter((d: any) => d.is_working_day)
    };
  }


  private _diffObject(current: Record<string, any>, original: Record<string, any> | null): Record<string, any> {
    if (!original) return { ...current };
    const diff: Record<string, any> = {};
    for (const key of Object.keys(current)) {
      if (JSON.stringify(current[key]) !== JSON.stringify(original[key])) {
        diff[key] = current[key];
      }
    }
    return diff;
  }

  private _buildAAMAddressPayload(): any | null {
    const current = this.practiceDetailsForm.getRawValue();
    const original = this._savedPracticeDetails;

    const addrChanged  = !original || JSON.stringify(current.address) !== JSON.stringify(original.address);
    const corrChanged  = !original || JSON.stringify(current.correspondence_address) !== JSON.stringify(original.correspondence_address);

    if (!addrChanged && !corrChanged) return null;

    const payload: any = { firm: {} };
    if (addrChanged)  payload.firm.address = current.address;
    if (corrChanged)  payload.firm.correspondence_address = current.correspondence_address;
    return payload;
  }

  private _buildAAMPracticePayload(): any | null {
    const current = this.practiceDetailsForm.getRawValue();
    const original = this._savedPracticeDetails;

    // For AAM (and SU Firm Profile), all editable fields live under the firm object in the API:
    // bar_council_id form control -> firm.reg_id
    // years_of_experience form control -> firm.years_of_incorporation
    // practice_areas and services_offered -> firm.practice_areas / firm.services_offered
    const firmPayload: any = {};

    if (!original || JSON.stringify(current.bar_council_id) !== JSON.stringify(original.bar_council_id)) {
      firmPayload.reg_id = current.bar_council_id;
    }
    if (!original || JSON.stringify(current.years_of_experience) !== JSON.stringify(original.years_of_experience)) {
      firmPayload.years_of_incorporation = current.years_of_experience ?? null;
    }
    if (!original || JSON.stringify(current.practice_areas) !== JSON.stringify(original.practice_areas)) {
      firmPayload.practice_areas = Array.isArray(current.practice_areas) ? current.practice_areas : [];
    }
    if (!original || JSON.stringify(current.services_offered) !== JSON.stringify(original.services_offered)) {
      firmPayload.services_offered = Array.isArray(current.services_offered) ? current.services_offered : [];
    }

    // Court enrollments (only relevant for non-firm / non-AAM paths in the API,
    // but include if the view ever exposes them; currently guarded in the template)
    const currentEnrollmentsAAM = this.courtEnrollments.value.map((r: any) => ({
      court_name: r.court_type,
      state: r.state || '',
      city: r.city || ''
    }));
    const savedEnrollmentsAAM = (original as any)?.court_enrollments || [];
    if (!original || JSON.stringify(currentEnrollmentsAAM) !== JSON.stringify(savedEnrollmentsAAM)) {
      firmPayload.court_enrollments = currentEnrollmentsAAM;
    }

    // Address fields also go under firm
    const addrChanged = !original || JSON.stringify(current.address) !== JSON.stringify(original.address);
    const corrChanged = !original || JSON.stringify(current.correspondence_address) !== JSON.stringify(original.correspondence_address);
    if (addrChanged) firmPayload.address = current.address;
    if (corrChanged) firmPayload.correspondence_address = current.correspondence_address;

    if (Object.keys(firmPayload).length === 0) return null;
    return { firm: firmPayload };
  }

  saveAdditionalInfo() {
    // if (!ensureActiveSubscription(this.confirmationDialogService)) return;
    this.submitted = true;

    // ── AAM: can edit bar_council_id, practice_areas, services_offered, and address in practice tab ─────────
    if (this.isAAM && this.activeTab === 'practice') {
      const practicePayload = this._buildAAMPracticePayload();
      if (!practicePayload) {
        // Nothing changed — just close edit mode
        this.isEditAdditionalInfo = false;
        this.submitted = false;
        return;
      }
      this.spinnerService.show();
      this.httpservice.sendPatchRequest(URLUtils.extendedProfile, practicePayload).subscribe(
        (res: any) => {
          this.spinnerService.hide();
          if (res.error === false) {
            this.toast.success(res.msg || 'Practice details updated successfully');
            this.isEditAdditionalInfo = false;
            this.submitted = false;
            this.getData();
          } else {
            this.toast.error(res.msg || 'Failed to save practice details');
          }
        },
        (error: HttpErrorResponse) => {
          this.spinnerService.hide();
          this.handleApiErrors(error);
        }
      );
      return;
    }

    // ── AAM: can edit awards in education tab ─────────
    if (this.isAAM && this.activeTab === 'education') {
      if (!this.educationForm.valid) {
        this.toast.error('Please fill all required fields in Awards');
        return;
      }
      const currentAwards = { awards: this.awards.value };
      const savedAwards = this._savedEducation;
      const awardDiff: any = {};
      
      if (!savedAwards || JSON.stringify(currentAwards.awards) !== JSON.stringify(savedAwards.awards)) {
        awardDiff.firm = { awards: currentAwards.awards };
      }

      if (Object.keys(awardDiff).length === 0) {
        // Nothing changed — just close edit mode
        this.isEditAdditionalInfo = false;
        this.submitted = false;
        return;
      }

      this.spinnerService.show();
      this.httpservice.sendPatchRequest(URLUtils.extendedProfile, awardDiff).subscribe(
        (res: any) => {
          this.spinnerService.hide();
          if (res.error === false) {
            this.toast.success(res.msg || 'Awards updated successfully');
            this.isEditAdditionalInfo = false;
            this.submitted = false;
            this.getData();
          } else {
            this.toast.error(res.msg || 'Failed to save awards');
          }
        },
        (error: HttpErrorResponse) => {
          this.spinnerService.hide();
          this.handleApiErrors(error);
        }
      );
      return;
    }

    //console.log('this.practiceDetailsForm called', this.practiceDetailsForm.value);
    console.log('this.activeTab',this.activeTab)
    if (this.practiceDetailsForm.invalid && this.activeTab === 'practice') {
      this.activeTab = 'practice';
      this.practiceDetailsForm.markAllAsTouched();
      this.toast.warning(
        'Please complete the required fields in Practice Details before saving.'
      );
      setTimeout(() => this.scrollToFirstInvalid(), 50);
      return;
    }

    // Validate court enrollments (custom validation for free-text fields)
    const courtError = this.validateCourtEnrollments();
    if (courtError) {
      this.activeTab = 'courts_cases';
      this.toast.warning(courtError);
      return;
    }

    const practiceValue = this.practiceDetailsForm.getRawValue();
    const savedPractice = this._savedPracticeDetails;

    // For SU Firm Profile view, practice_areas, services_offered, reg_id, and years_of_incorporation
    // live under the firm object. For all other roles they are top-level profile fields.
    const practicePayload: any = {};

    if (this.isFirmProfileView) {
      // SU Firm Profile: route firm-level fields under firm{}
      const firmPayload: any = {};

      if (!savedPractice || JSON.stringify(practiceValue.bar_council_id) !== JSON.stringify(savedPractice.bar_council_id)) {
        firmPayload.reg_id = practiceValue.bar_council_id;
      }
      if (!savedPractice || JSON.stringify(practiceValue.years_of_experience) !== JSON.stringify(savedPractice.years_of_experience)) {
        firmPayload.years_of_incorporation = practiceValue.years_of_experience ?? null;
      }
      if (!savedPractice || JSON.stringify(practiceValue.practice_areas) !== JSON.stringify(savedPractice.practice_areas)) {
        firmPayload.practice_areas = Array.isArray(practiceValue.practice_areas) ? practiceValue.practice_areas : [];
      }
      if (!savedPractice || JSON.stringify(practiceValue.services_offered) !== JSON.stringify(savedPractice.services_offered)) {
        firmPayload.services_offered = Array.isArray(practiceValue.services_offered) ? practiceValue.services_offered : [];
      }

      // Court enrollments — include for firm profile view (guards in template already prevent adding
      // them without a practice area, but we diff and send whatever is in the FormArray)
      const firmCourtEnrollments = this.courtEnrollments.value.map((r: any) => ({
        court_name: r.court_type,
        state: r.state || '',
        city: r.city || ''
      }));
      const savedFirmCourts = savedPractice?.court_enrollments || [];
      if (!savedPractice || JSON.stringify(firmCourtEnrollments) !== JSON.stringify(savedFirmCourts)) {
        firmPayload.court_enrollments = firmCourtEnrollments;
      }

      const addrChanged = !savedPractice || JSON.stringify(practiceValue.address) !== JSON.stringify(savedPractice.address);
      const corrChanged = !savedPractice || JSON.stringify(practiceValue.correspondence_address) !== JSON.stringify(savedPractice.correspondence_address);
      if (addrChanged) firmPayload.address = practiceValue.address;
      if (corrChanged) firmPayload.correspondence_address = practiceValue.correspondence_address;

      if (Object.keys(firmPayload).length > 0) {
        practicePayload.firm = firmPayload;
      }
    } else {
      // All other roles (TM, GH, Solo, etc.): top-level scalar fields
      const scalarKeys = ['bar_council_id', 'years_of_experience', 'practice_areas', 'services_offered', 'languages_spoken', 'cases_handled'];
      for (const key of scalarKeys) {
        if (!savedPractice || JSON.stringify(practiceValue[key]) !== JSON.stringify(savedPractice[key])) {
          if (key === 'practice_areas' || key === 'services_offered' || key === 'languages_spoken' || key === 'cases_handled') {
            practicePayload[key] = Array.isArray(practiceValue[key]) ? practiceValue[key] : [];
          } else {
            // For numeric fields (years_of_experience), preserve null so the API
            // receives null (unset) rather than 0 when the field is left empty.
            practicePayload[key] = practiceValue[key] ?? null;
          }
        }
      }

      // Court enrollments diff
      const currentEnrollments = this.courtEnrollments.value.map((r: any) => ({
        court_name: r.court_type,  // API expects court_name but form control is court_type
        // court_type: r.court_type,
        state: r.state || '',
        city: r.city || ''
      }));
      const savedEnrollments = savedPractice?.court_enrollments || [];
      if (!savedPractice || JSON.stringify(currentEnrollments) !== JSON.stringify(savedEnrollments)) {
        practicePayload['court_enrollments'] = currentEnrollments;
      }

      // Address diff
      const addrChanged = !savedPractice || JSON.stringify(practiceValue.address) !== JSON.stringify(savedPractice.address);
      const corrChanged = !savedPractice || JSON.stringify(practiceValue.correspondence_address) !== JSON.stringify(savedPractice.correspondence_address);
      if (addrChanged || corrChanged) {
        practicePayload.firm = { ...(practicePayload.firm || {}) };
        if (addrChanged)  practicePayload.firm.address = practiceValue.address;
        if (corrChanged)  practicePayload.firm.correspondence_address = practiceValue.correspondence_address;
      }

      // ── consultation_fee / billing_currency ───────────────────────────────
      // These two fields live in personalInfoForm, but for SU "My Profile"
      // (non-Firm-Profile, non-AAM) they are rendered inside the Practice
      // Details tab's "Professional Information" row (nested formGroup), so
      // they must be diffed and included here as well — otherwise edits made
      // on this tab are silently lost and never reach the payload.
      const personalCurrent = this.personalInfoForm.getRawValue();
      const personalSaved = this._savedPersonalInfo;
      const feeChanged = !personalSaved || JSON.stringify(personalCurrent.consultation_fee) !== JSON.stringify(personalSaved.consultation_fee);
      const currencyChanged = !personalSaved || JSON.stringify(personalCurrent.billing_currency) !== JSON.stringify(personalSaved.billing_currency);

      if (feeChanged) {
        practicePayload.consultation_fee = personalCurrent.consultation_fee
          ? {
              amount: personalCurrent.consultation_fee,
              symbol: personalCurrent.billing_currency === 'IndianRupee(INR)' ? '₹' : ''
            }
          : null;
      }
      if (currencyChanged) {
        practicePayload.firm = { ...(practicePayload.firm || {}), billing_currency: personalCurrent.billing_currency || 'IndianRupee(INR)' };
        // Keep the symbol on consultation_fee in sync if both changed together
        if (practicePayload.consultation_fee) {
          practicePayload.consultation_fee.symbol = personalCurrent.billing_currency === 'IndianRupee(INR)' ? '₹' : '';
        }
      }
    }

    // For the practice tab itself there is no secondary payload.
    let secondaryPayload: any = null;

    if (this.activeTab === 'education') {
      if (!this.educationForm.valid) {
        this.toast.error('Please fill all required fields in Education & Awards');
        return;
      }
      // Diff education payload
      const currentEdu = {
        education: this.education.value,
        certifications: this.certifications.value,
        awards: this.awards.value
      };
      const savedEdu = this._savedEducation;
      const eduDiff: any = {};
      if (!savedEdu || JSON.stringify(currentEdu.education) !== JSON.stringify(savedEdu.education)) {
        eduDiff.education = currentEdu.education;
      }
      if (!savedEdu || JSON.stringify(currentEdu.certifications) !== JSON.stringify(savedEdu.certifications)) {
        eduDiff.certifications = currentEdu.certifications;
      }
      // For SU Firm Profile view and AAM, awards belong under firm in the API
      if (!savedEdu || JSON.stringify(currentEdu.awards) !== JSON.stringify(savedEdu.awards)) {
        if (this.isFirmProfileView || this.isAAM) {
          eduDiff.firm = { ...(eduDiff.firm || {}), awards: currentEdu.awards };
        } else {
          eduDiff.awards = currentEdu.awards;
        }
      }
      if (Object.keys(eduDiff).length > 0) {
        secondaryPayload = eduDiff;
      }
    } else if (this.activeTab === 'availability') {
      if (!this.availabilityForm.valid) {
        this.toast.error('Please fill all required fields in Availability');
        return;
      }
      const validationResult = this.validateAvailability();
      if (!validationResult.valid) {
        this.toast.error(validationResult.message);
        return;
      }

      const availabilityValue = this.availabilityForm.value;

      const currentAvail = {
        ...availabilityValue,
        weekly_schedule: availabilityValue.weekly_schedule
          .filter((day: any) => day.is_working_day === true)
          .map((day: any) => {
            const work = day.work_slots?.[0];

            return {
              ...day,
              expert_slots: (day.expert_slots || []).filter((slot: any) => {
                if (!work) return false;
                return (
                  slot.start_time >= work.start_time &&
                  slot.end_time <= work.end_time
                );
              })
            };
          })
      };

      // console.log(currentAvail);

      // Only send availability payload if something changed
      if (!this._savedAvailability || JSON.stringify(currentAvail) !== JSON.stringify(this._savedAvailability)) {
        secondaryPayload = { availability: currentAvail };
      }
    }

    // If nothing changed at all, just close edit mode
    if (Object.keys(practicePayload).length === 0 && !secondaryPayload) {
      this.isEditAdditionalInfo = false;
      this.submitted = false;
      return;
    }

    this.spinnerService.show();

    if (Object.keys(practicePayload).length === 0 && secondaryPayload) {
      this.httpservice.sendPatchRequest(URLUtils.extendedProfile, secondaryPayload).subscribe(
        (secondaryRes: any) => {
          this.spinnerService.hide();
          if (secondaryRes.error === false) {
            this.toast.success(secondaryRes.msg || 'Additional Information updated successfully');
            this.isEditAdditionalInfo = false;
            this.submitted = false;
            this.getData();
          } else {
            this.toast.error(secondaryRes.msg || 'Failed to save ' +
              (this.activeTab === 'education' ? 'Education & Awards' : 'Availability'));
          }
        },
        (error: HttpErrorResponse) => {
          this.spinnerService.hide();
          this.handleApiErrors(error);
        }
      );
      return;
    }

    this.httpservice.sendPatchRequest(URLUtils.extendedProfile, practicePayload).subscribe(
      (practiceRes: any) => {
        if (practiceRes.error !== false) {
          this.spinnerService.hide();
          this.toast.error(practiceRes.msg || 'Failed to save Practice Details');
          return;
        }

        if (!secondaryPayload) {
          this.spinnerService.hide();
          this.toast.success(practiceRes.msg || 'Additional Information updated successfully');
          this.isEditAdditionalInfo = false;
          this.submitted = false;
          this.getData();
          return;
        }

        this.httpservice.sendPatchRequest(URLUtils.extendedProfile, secondaryPayload).subscribe(
          (secondaryRes: any) => {
            this.spinnerService.hide();
            if (secondaryRes.error === false) {
              this.toast.success(secondaryRes.msg || 'Additional Information updated successfully');
              this.isEditAdditionalInfo = false;
              this.submitted = false;
              this.getData();
            } else {
              this.toast.error(secondaryRes.msg || 'Failed to save ' +
                (this.activeTab === 'education' ? 'Education & Awards' : 'Availability'));
            }
          },
          (error: HttpErrorResponse) => {
            this.spinnerService.hide();
            this.handleApiErrors(error);
          }
        );
      },
      (error: HttpErrorResponse) => {
        this.spinnerService.hide();
        this.handleApiErrors(error);
      }
    );
  }

  scrollToSection(section: ElementRef) {
  if (!section) return;

  setTimeout(() => {
    section.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, 100); // wait for DOM render
}


handleCompleteNow() {
  // console.log(this.basic_profile);
  // console.log(this.firm_details);

  const nextStep = (this.isFirmProfileView || this.isAAM)
    ? this.firm_details?.profile_completion?.next_step
    : this.basic_profile?.profile_completion?.next_step;
  const active = nextStep?.redirect_tab;

  if (active) {
    // Only update activeTab when redirecting to an Additional Information tab
    // (practice / education / availability / subscription).
    // 'profile' is NOT a tab in that section — setting activeTab to 'profile'
    // removes the active highlight from Practice Details entirely.
    // Map API redirect_tab values to internal activeTab values where they differ.
    const tabMap: Record<string, string> = { 'court': 'courts_cases' };
    const internalTab = tabMap[active] ?? active;
    if (active !== 'profile') {
      this.activeTab = internalTab;
      if (internalTab === 'subscription' && !this.bankAccountFetched) {
        this.loadBankAccountDetails();
      }
    }

    const missingFields: string[] = nextStep?.missing_fields || [];

    if (active === 'profile') {
      // When the only missing field is profile_pic, open the photo editor;
      // otherwise open the personal-info form.
      if (missingFields.length === 1 && missingFields[0] === 'profile_pic') {
        this.editProfilePicture();
      } else {
        this.editPersonalInfo();
        // Focus the first missing personal-info field after edit mode opens
        setTimeout(() => this.focusMissingField(missingFields), 150);
      }
    } else {
      this.editAdditionalInfo();

      // Give Angular enough time to render the newly-active tab's DOM before
      // trying to locate and focus the missing field element.
      setTimeout(() => {
        this.focusMissingField(missingFields);
      }, 300);
    }

    return;
  }

  // 🔁 Fallback (old logic if backend not present)
  // if (this.isPersonalSectionIncomplete()) {
  //   this.activeTab = 'profile';
  //   this.editPersonalInfo();
  //   return;
  // }

  // const sectionMap = [
  //   { check: this.isPracticeSectionIncomplete(), tab: 'practice' },
  //   { check: this.isEducationSectionIncomplete(), tab: 'education' },
  //   { check: this.isAvailabilitySectionIncomplete(), tab: 'availability' }
  // ];

  // for (const section of sectionMap) {
  //   if (section.check) {
  //     this.activeTab = section.tab;
  //     this.editAdditionalInfo();

  //     setTimeout(() => {
  //       if (this.additionalSection) {
  //         this.scrollToSection(this.additionalSection);
  //       }
  //     }, 0);

  //     return;
  //   }
  // }

  // // Final fallback
  // this.editAdditionalInfo();
}

/**
 * Maps API missing_fields names to DOM element IDs and scrolls/focuses
 * the first matched field. Called after edit mode has been opened.
 *
 * API field name to element ID mapping (covers all known aliases the backend sends):
 *   "bio"                   -> field-bio_description (entity) | field-bio_solo (solo)
 *   "bio_description"       -> field-bio_description | field-bio_solo
 *   "firm_description"      -> field-firm_description
 *   "practice_areas"        -> field-practice_areas  (opens dropdown then focuses search)
 *   "services_offered"      -> field-services_offered (focuses inline input)
 *   "years_of_incorporation"-> field-years_of_incorporation
 *   "education"             -> field-education
 *   "certifications"        -> field-certifications
 *   "awards"                -> field-awards
 *   "fullname"              -> field-fullname
 *   "contact_person"        -> field-contact_person | field-contact_person_solo
 *   "reg_id"                -> field-bar_council_id  (firm maps reg_id to bar_council_id control)
 *   "bar_council_id"        -> field-bar_council_id
 *   "website"               -> field-website (entity) | field-website_solo (solo)
 *   "availability"          -> field-availability
 *   "working_hours"         -> field-availability (API key for missing working hours)
 */
private focusMissingField(missingFields: string[]): void {
  if (!missingFields || missingFields.length === 0) return;

  // Map API field names to one or more candidate element IDs.
  // Multiple IDs per key handle solo vs entity category (only one is visible at a time).
  const fieldIdMap: Record<string, string[]> = {
    'bio':                    ['field-bio_description', 'field-bio_solo'],
    'bio_description':        ['field-bio_description', 'field-bio_solo'],
    'firm_description':       ['field-firm_description'],
    'practice_areas':         ['field-practice_areas'],
    'services_offered':       ['field-services_offered'],
    'years_of_incorporation': ['field-years_of_incorporation'],
    // years_of_experience is the API key for non-firm profiles; maps to the same DOM input
    'years_of_experience':    ['field-years_of_incorporation'],
    // languages_spoken uses a chip-input container that gets its id added in the HTML
    'languages_spoken':       ['field-languages_spoken'],
    'education':              ['field-education'],
    'certifications':         ['field-certifications'],
    'awards':                 ['field-awards'],
    'court_enrollments':      ['field-court_enrollments'],
    'cases_handled':          ['field-cases_handled'],
    'consultation_fee':       ['field-consultation_fee'],
    'fullname':               ['field-fullname'],
    'contact_person':         ['field-contact_person', 'field-contact_person_solo'],
    // reg_id is stored as bar_council_id in the form control (firm context maps reg_id -> bar_council_id)
    'reg_id':                 ['field-bar_council_id'],
    'bar_council_id':         ['field-bar_council_id'],
    // website appears in both entity and solo category forms; try both, skip hidden one
    'website':                ['field-website', 'field-website_solo'],
    // availability tab edit form; the API sends 'working_hours' as the missing_field key
    'availability':           ['field-availability'],
    'working_hours':          ['field-availability'],
  };

  // The API sometimes sends a different alias for a missing field than the
  // canonical key used throughout this method (fieldIdMap keys + the literal
  // field === '...' checks below). Normalize known aliases here so the rest
  // of the logic doesn't need to know about every API-side naming variant.
  // e.g. the "education" section's missing_fields entry is "education_details",
  // not "education", which previously fell through to a non-existent
  // "field-education_details" id and silently did nothing.
  const fieldAliasMap: Record<string, string> = {
    'education_details': 'education',
  };

  for (const rawField of missingFields) {
    const field = fieldAliasMap[rawField] ?? rawField;
    // Resolve candidate IDs — fall back to field-{name} for unmapped keys
    const candidateIds: string[] = fieldIdMap[field] ?? [`field-${field}`];

    for (const elementId of candidateIds) {
      // ── Pre-guard: court_enrollments empty-array case ─────────────────────
      // When court_enrollments is empty, #field-court_enrollments is a
      // zero-height div whose offsetParent is null — it would be skipped by
      // the guard below. Handle it here BEFORE the offsetParent check so we
      // can add a blank row, scroll to the section, and open the dropdown.
      if (field === 'court_enrollments' && this.courtEnrollments.length === 0) {
        // Scroll to the section header (always visible) before adding the row
        const section = document.querySelector<HTMLElement>('.court-practice-edit-section');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        this.addCourtEnrollment();

        // Trigger the suggest API now (mirrors toggleCourtTypeDropdown's behaviour)
        // so that API-driven options are ready by the time the dropdown renders.
        this.fetchCourtSuggestions();

        // Give Angular one tick to render the new *ngFor row, then open the
        // Court Type dropdown. The actual scroll happens further below, once
        // the dropdown panel itself has rendered — scrolling the row here
        // would only account for the collapsed row's height, not the panel
        // that opens beneath it, leaving the panel clipped at the bottom.
        setTimeout(() => {
          const container = document.getElementById(elementId);
          if (!container) return;
          this.activeCourtTypeDropdown = 0;
          setTimeout(() => {
            // Scroll the dropdown panel itself (now rendered with its
            // options list) into view so the whole panel is visible,
            // not just the row it's attached to.
            const dropdownPanel = container.querySelector<HTMLElement>('.court-select-dropdown');
            (dropdownPanel ?? container).scrollIntoView({ behavior: 'smooth', block: 'end' });
            const searchInput = container.querySelector<HTMLInputElement>('.court-select-search input');
            if (searchInput) {
              searchInput.focus();
            } else {
              const trigger = container.querySelector<HTMLElement>('.court-select-trigger');
              if (trigger) trigger.focus();
            }
          }, 50);
        }, 100);
        return;
      }
      // ── End pre-guard ─────────────────────────────────────────────────────

      const el = document.getElementById(elementId) as HTMLElement | null;
      // Skip elements not in the DOM or hidden by *ngIf (offsetParent is null)
      if (!el || el.offsetParent === null) continue;

      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

      // Direct focusable elements (input, textarea, select)
      if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement
      ) {
        el.focus();
        return;
      }

      // Special case: practice_areas uses a custom chip-dropdown whose search
      // input only exists when the dropdown is open. Open it programmatically
      // so the user can immediately type and select.
      if (field === 'practice_areas') {
        this.showPracticeAreaDropdown = true;
        setTimeout(() => {
          const searchInput = el.querySelector<HTMLInputElement>('.dropdown-search input');
          if (searchInput) {
            searchInput.focus();
          } else {
            // Fallback: focus the tabindex-bearing trigger div
            const trigger = el.querySelector<HTMLElement>('[tabindex="0"]');
            if (trigger) trigger.focus();
          }
        }, 50);
        return;
      }

      // Special case: services_offered uses a custom chip-dropdown (API-driven)
      // whose dropdown panel is toggled via showServiceDropdown. Open it
      // programmatically and focus the inline search input so the user can
      // immediately start typing to find/select a service.
      if (field === 'services_offered') {
        this.showServiceDropdown = true;
        if (!this.serviceSearchMode && !this.serviceInput.trim()) {
          this.fetchSuggestedServices();
        }
        setTimeout(() => {
          // The service input is the text field inside the service-offered-wrapper
          const serviceInput = el.querySelector<HTMLInputElement>('input[type="text"]');
          if (serviceInput) {
            serviceInput.focus();
          } else {
            // Fallback: focus any focusable element inside the wrapper
            const trigger = el.querySelector<HTMLElement>('[tabindex="0"]');
            if (trigger) trigger.focus();
          }
        }, 50);
        return;
      }

      // Special cases: education / certifications / awards are FormArrays that
      // render rows via *ngFor. When empty there are no inputs in the DOM yet.
      // Add a blank row first, then wait one tick for Angular to render it,
      // then scroll to the new row and focus its first input.
      if (field === 'education' || field === 'certifications' || field === 'awards') {
        const isEmptyArray =
          (field === 'education'      && this.education.length === 0) ||
          (field === 'certifications' && this.certifications.length === 0) ||
          (field === 'awards'         && this.awards.length === 0);

        if (isEmptyArray) {
          if (field === 'education')      { this.addEducation(); }
          else if (field === 'certifications') { this.addCertification(); }
          else if (field === 'awards')    { this.addAward(); }

          // Wait for Angular to render the new *ngFor row, then focus first input
          setTimeout(() => {
            const container = document.getElementById(elementId);
            if (!container) return;
            container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            const firstInput = container.querySelector<HTMLInputElement>(
              'input:not([type="hidden"]):not([readonly])'
            );
            if (firstInput) firstInput.focus();
          }, 50);
          return;
        }

        // Array already has rows — just scroll and focus first input
        const firstInput = el.querySelector<HTMLInputElement>(
          'input:not([type="hidden"]):not([readonly])'
        );
        if (firstInput) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          firstInput.focus();
        }
        return;
      }

      // Special case: court_enrollments is a FormArray rendered via *ngFor.
      // Rows already exist (empty-array case is handled before the offsetParent
      // guard further below) — open the Court Type dropdown for row 0, then
      // scroll the dropdown panel itself into view once it has rendered, and
      // focus its search input (mirrors the practice_areas behaviour).
      if (field === 'court_enrollments') {
        // Trigger the suggest API now (mirrors toggleCourtTypeDropdown's behaviour)
        // so that API-driven options are ready by the time the dropdown renders.
        this.fetchCourtSuggestions();
        this.activeCourtTypeDropdown = 0;
        setTimeout(() => {
          // Scroll the dropdown panel itself (now rendered with its options
          // list) into view so the whole panel is visible, not just the row —
          // scrolling the row before the panel opens left the panel clipped.
          const dropdownPanel = el.querySelector<HTMLElement>('.court-select-dropdown');
          (dropdownPanel ?? el).scrollIntoView({ behavior: 'smooth', block: 'end' });
          const searchInput = el.querySelector<HTMLInputElement>('.court-select-search input');
          if (searchInput) {
            searchInput.focus();
          } else {
            const trigger = el.querySelector<HTMLElement>('.court-select-trigger');
            if (trigger) trigger.focus();
          }
        }, 50);
        return;
      }

      // Special case: cases_handled is a free-text chip field — the inline
      // input is always present in the DOM; just scroll to and focus it.
      if (field === 'cases_handled') {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        const chipInput = el.querySelector<HTMLInputElement>('.chip-inline-input');
        if (chipInput) chipInput.focus();
        return;
      }

      // Special case: languages_spoken is a chip-input field with an inline text input
      if (field === 'languages_spoken') {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        const langInput = el.querySelector<HTMLInputElement>('input.chip-inline-input');
        if (langInput) langInput.focus();
        return;
      }

      // Special case: working_hours / availability — the #field-availability form sits outside
      // the inner-card scroll area and may have offsetParent = null even after rendering.
      // Scroll it into view unconditionally, then focus the first day-checkbox so the user
      // can immediately start toggling days without any extra click.
      if (field === 'working_hours' || field === 'availability') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const firstCheckbox = el.querySelector<HTMLInputElement>('input[type="checkbox"]');
        if (firstCheckbox) firstCheckbox.focus();
        return;
      }

      // Container elements: focus the first always-present, non-readonly descendant
      const focusable = el.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([readonly]), textarea:not([readonly]), select:not([disabled]), [tabindex="0"]'
      );
      if (focusable) {
        focusable.focus();
      } else {
        // Last resort: give the container a temporary tabindex so it can receive focus
        el.setAttribute('tabindex', '-1');
        el.focus();
      }
      return;
    }
  }
}
isPersonalSectionIncomplete(): boolean {
  const p = this.basic_profile;
  const f = this.firm_details;

  if (!p || !f) return true;

  return (
    !f.contact_person ||
    !f.contact_phone ||
    !f.billing_currency ||
    !p.consultation_fee?.amount ||
    !(this.isFirmProfileView || this.isAAM ? f.firm_description?.trim() : p.bio_description?.trim()) ||  // optional but affects %
    !f.website?.trim() ||                // optional but affects %
    !this.profilePictureUrl              // profile pic missing
  );
}
isPracticeSectionIncomplete(): boolean {
  const p = this.basic_profile;
  const f = this.firm_details;

  if (!p || !f) return true;

  return (
    !p.practice_areas?.length ||
    !p.languages_spoken?.length ||
    !p.services_offered?.length ||
    !f.address?.house_flat_no ||
    !f.address?.city_town ||
    !f.address?.zipcode ||
    !p.bar_council_id
  );
}
 isEducationSectionIncomplete(): boolean {
  const p = this.basic_profile;
  if (!p) return true;

  return (   !p.education?.length ||
    !p.certifications?.length ||
    !p.awards?.length


  );
}
isAvailabilitySectionIncomplete(): boolean {
  const p = this.basic_profile;
  if (!p?.availability?.weekly_schedule) return true;

  const workingDays = p.availability.weekly_schedule.filter(
    (d: any) => d.is_working_day
  );

  return workingDays.length === 0;
}

  
getContactPersonLabel(): string {
    return this.isSoloCategory ? 'Name' : 'Contact Name';
  }

convertTo12Hour(time24: string): string {
  if (!time24) return '';
  
  const [hours24, minutes] = time24.split(':').map(Number);
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
}

allowOnlyNumbers(event: KeyboardEvent) {
  const char = event.key;
  if (!/^[0-9]$/.test(char)) {
    event.preventDefault();
  }
}

  blockExperienceKeys(event: KeyboardEvent) {
    // Allow control keys: Backspace, Delete, Tab, Arrow keys, Home, End
    const controlKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (controlKeys.includes(event.key)) return;
    // Allow Ctrl/Cmd shortcuts (copy, paste, select all, etc.)
    if (event.ctrlKey || event.metaKey) return;
    // Block anything that is not a single digit 0-9
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  limitExperience(event: any) {
    const input = event.target as HTMLInputElement;

    // Remove every character that is not a digit
    let clean = input.value.replace(/[^0-9]/g, '');

    // Enforce max 3 digits
    if (clean.length > 3) {
      clean = clean.slice(0, 3);
    }

    // Convert to number; empty string stays empty, never negative
    const numeric = clean === '' ? null : Math.max(0, parseInt(clean, 10));

    // Write sanitised value back to DOM and form control
    input.value = numeric === null ? '' : String(numeric);
    this.practiceDetailsForm.get('years_of_experience')?.setValue(
      numeric,   // null when the field is cleared, a number otherwise
      { emitEvent: false }
    );
  }

convertTo24Hour(time12: string): string {
  if (!time12) return '';
  
  const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return time12;
  
  let [, hours, minutes, period] = match;
  let hours24 = parseInt(hours);
  
  if (period.toUpperCase() === 'PM' && hours24 !== 12) {
    hours24 += 12;
  } else if (period.toUpperCase() === 'AM' && hours24 === 12) {
    hours24 = 0;
  }
  
  return `${String(hours24).padStart(2, '0')}:${minutes}`;
}

generate12HourTimeOptions(): string[] {
  const times: string[] = [];
  
  for (let hour = 0; hour < 24; hour++) {
    for (let minute of [0, 30]) {
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      const timeString = `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
      times.push(timeString);
    }
  }
  
  return times;
}

onStartTimeChange(dayIndex: number, slotIndex: number, selectedTime12: string) {
  if (!selectedTime12) return;
  
  const time24 = this.convertTo24Hour(selectedTime12);
  const workSlots = this.getWorkSlots(dayIndex);
  const slot = workSlots.at(slotIndex) as FormGroup;
  
  if (slot) {
    slot.patchValue({ start_time: time24 }, { emitEvent: true });
    
    // Clear end time if it's now before or equal to start time
    const endTime24 = slot.get('end_time')?.value;
    if (endTime24 && endTime24 <= time24) {
      slot.patchValue({ end_time: '' }, { emitEvent: true });
    }

    // Purge expert_slots that fall outside the new work window
    this._pruneExpertSlotsForDay(dayIndex);
  }
}

onEndTimeChange(dayIndex: number, slotIndex: number, selectedTime12: string) {
  if (!selectedTime12) return;
  
  const time24 = this.convertTo24Hour(selectedTime12);
  const workSlots = this.getWorkSlots(dayIndex);
  const slot = workSlots.at(slotIndex) as FormGroup;
  
  if (slot) {
    slot.patchValue({ end_time: time24 }, { emitEvent: true });

    // Purge expert_slots that fall outside the new work window
    this._pruneExpertSlotsForDay(dayIndex);
  }
}

getDisplayTime(time24: string): string {
  if (!time24) return '';
  return this.convertTo12Hour(time24);
}

/**
 * Removes any expert_slots (excluded slots) from a day that are no longer
 * within the day's current work window. Called whenever start or end time
 * changes so the displayed availability count stays accurate and can never
 * reach 0 due to a stale exclusion list.
 */
private _pruneExpertSlotsForDay(dayIndex: number): void {
  const day = this.weeklySchedule.at(dayIndex) as FormGroup;
  const workSlot = this.getWorkSlots(dayIndex).at(0);
  if (!workSlot) return;

  const workStart = workSlot.get('start_time')?.value;
  const workEnd   = workSlot.get('end_time')?.value;
  if (!workStart || !workEnd) return;

  const existingExcluded: { start_time: string; end_time: string }[] =
    day.get('expert_slots')?.value || [];

  const pruned = existingExcluded.filter(
    (s: { start_time: string; end_time: string }) =>
      s.start_time >= workStart && s.end_time <= workEnd
  );

  // Compute slots that would remain after pruned exclusions
  const allSlots = this.generateTimeSlots(workStart, workEnd);
  const remaining = allSlots.filter(
    s => !pruned.some(
      (ex: { start_time: string; end_time: string }) =>
        ex.start_time === s.start && ex.end_time === s.end
    )
  );

  // If the new window is so small that all pruned exclusions still consume
  // every slot, drop all exclusions entirely to guarantee at least 1 slot.
  const finalExcluded = remaining.length > 0 ? pruned : [];

  day.patchValue({ expert_slots: finalExcluded }, { emitEvent: false });
}

generateThirtyMinuteSlots(startTime: string, endTime: string): { start: string; end: string }[] {
  const slots: { start: string; end: string }[] = [];
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  let currentHour = startHour;
  let currentMin = startMin;
  
  while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
    const start = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
    currentMin += 30;
    if (currentMin >= 60) {
      currentMin = 0;
      currentHour += 1;
    }
    const end = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
    
    if (currentHour < endHour || (currentHour === endHour && currentMin <= endMin)) {
      slots.push({ start, end });
    }
  }
  
  return slots;
}

calculateAvailableHours(startTime: string, endTime: string, excludedSlots: string[] = []): number {
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  const totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
  const excludedMinutes = excludedSlots.length * 30;
  
  return (totalMinutes - excludedMinutes) / 60;
}
// Check if exclude icon should be enabled
canExcludeSlots(dayIndex: number): boolean {
  const day = this.weeklySchedule.at(dayIndex) as FormGroup;
  const workSlots = day.get('work_slots') as FormArray;
  
  if (workSlots.length === 0) return false;
  
  // Calculate total available hours
  let totalHours = 0;
  workSlots.controls.forEach((slot: AbstractControl) => {
    const slotGroup = slot as FormGroup;
    const startTime = slotGroup.get('start_time')?.value;
    const endTime = slotGroup.get('end_time')?.value;
    
    if (startTime && endTime) {
      totalHours += this.calculateAvailableHours(startTime, endTime);
    }
  });
  
  return totalHours >= this.MIN_AVAILABILITY_HOURS;
}

// Get tooltip message for exclude icon
getExcludeTooltip(dayIndex: number): string {
  if (this.canExcludeSlots(dayIndex)) {
    return 'Exclude specific time slots';
  }
  return 'Availability must be at least 4 hours to exclude slots';
}

openExcludePanel(dayIndex: number): void {
  if (this.activeExcludeDayIndex === dayIndex) {
    this.closeExcludePanel();
    return;
  }

  this.activeExcludeDayIndex = dayIndex;
  
  // Load existing excluded slots
  const day = this.weeklySchedule.at(dayIndex) as FormGroup;
  const existingSlots = day.get('expert_slots')?.value || [];
  
  // Convert existing slots to slot keys
  this.tempSelectedExcludeSlots = existingSlots.map((slot: any) => 
    `${slot.start_time}-${slot.end_time}`
  );
  
  this.buildExcludeSlots(dayIndex);
}

closeExcludePanel(): void {
  this.activeExcludeDayIndex = null;
  this.tempSelectedExcludeSlots = [];
}
buildExcludeSlots(dayIndex: number): void {
  const slot = this.getWorkSlots(dayIndex).at(0);
  if (!slot) return;

  const start = slot.get('start_time')?.value;
  const end = slot.get('end_time')?.value;

  this.availableExcludeSlots = this.generateTimeSlots(start, end);
}
generateTimeSlots(start: string, end: string): any[] {
  const slots = [];
  let current = this.toMinutes(start);
  const endMin = this.toMinutes(end);

  while (current + 30 <= endMin) {
    slots.push({
      start: this.fromMinutes(current),
      end: this.fromMinutes(current + 30)
    });
    current += 30;
  }

  return slots;
}

toMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

fromMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}


// Toggle exclude slot selection
toggleExcludeSlot(slotKey: string) {
  const index = this.tempSelectedExcludeSlots.indexOf(slotKey);
  
  if (index > -1) {
    // Remove from selection
    this.tempSelectedExcludeSlots.splice(index, 1);
  } else {
    // Check if max limit reached
    if (this.tempSelectedExcludeSlots.length >= this.MAX_EXCLUDE_SLOTS) {
      this.toast.warning(`You can exclude maximum ${this.MAX_EXCLUDE_SLOTS} slots (2 hours) per day`);
      return;
    }
    // Prevent excluding all available slots — at least 1 slot must remain
    const totalSlots = this.availableExcludeSlots.length;
    if (this.tempSelectedExcludeSlots.length + 1 >= totalSlots) {
      this.toast.warning('At least one slot must remain available. You cannot exclude all time slots.');
      return;
    }
    // Add to selection
    this.tempSelectedExcludeSlots.push(slotKey);
  }
}

// Check if slot is selected
isSlotSelected(slotKey: string): boolean {
  return this.tempSelectedExcludeSlots.includes(slotKey);
}

saveExcludedSlots() {
  if (this.activeExcludeDayIndex === null) return;
  
  // Guard: do not allow excluding all available slots — at least 1 must remain
  const totalSlots = this.availableExcludeSlots.length;
  if (this.tempSelectedExcludeSlots.length >= totalSlots) {
    this.toast.error('You must keep at least one available slot. Availability cannot be set to 0 hours.');
    return;
  }

  const day = this.weeklySchedule.at(this.activeExcludeDayIndex) as FormGroup;
  
  // Convert selected slots to {start_time, end_time} format
  const expertSlots = this.tempSelectedExcludeSlots.map(slotKey => {
    const [start, end] = slotKey.split('-');
    return { start_time: start, end_time: end };
  });
  
  // Update the form with expert_slots
  day.patchValue({ expert_slots: expertSlots });
  
  this.toast.success('Excluded slots saved successfully');
  this.closeExcludePanel();
}

// Get excluded slots for a day
getExcludedSlots(dayIndex: number): { start_time: string; end_time: string }[] {
  const day = this.weeklySchedule.at(dayIndex) as FormGroup;
  return day.get('expert_slots')?.value || [];
}

// Format excluded slots for display
formatExcludedSlots(excludedSlots: { start_time: string; end_time: string }[]): string {
  return excludedSlots.map(slot => `${slot.start_time}–${slot.end_time}`).join(', ');
}

// Calculate total blocked time
calculateBlockedTime(excludedSlots: { start_time: string; end_time: string }[]): string {
  const hours = (excludedSlots.length * 30) / 60;
  const minutes = (excludedSlots.length * 30) % 60;
  
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
}

// Get slot key for comparison
getSlotKey(slot: { start: string; end: string }): string {
  return `${slot.start}-${slot.end}`;
}

  // Initialize weekly schedule
  initializeWeeklySchedule() {
    const scheduleArray = this.availabilityForm.get('weekly_schedule') as FormArray;
    this.daysOfWeek.forEach(day => {
      scheduleArray.push(this.createDaySchedule(day.value, day.name, false));
    });
  }

  // Create day schedule form group
  createDaySchedule(dayOfWeek: number, dayName: string, isWorking: boolean, date: string = ''): FormGroup {
  return this.fb.group({
    date: [date],
    day_of_week: [dayOfWeek],
    day_name: [dayName],
    date_label: [dayName],
    is_working_day: [isWorking],
    work_slots: this.fb.array(isWorking ? [this.createWorkSlot()] : []),
    expert_slots: [[]] // Add expert_slots array
  });
}

  // Create work slot form group
  createWorkSlot(startTime: string = '09:00', endTime: string = '17:00'): FormGroup {
    return this.fb.group({
      start_time: [startTime, Validators.required],
      end_time: [endTime, Validators.required]
    });
  }

  // Get FormArrays
  get education(): FormArray {
    return this.educationForm.get('education') as FormArray;
  }

  get certifications(): FormArray {
    return this.educationForm.get('certifications') as FormArray;
  }

  get awards(): FormArray {
    return this.educationForm.get('awards') as FormArray;
  }

  get weeklySchedule(): FormArray {
    return this.availabilityForm.get('weekly_schedule') as FormArray;
  }

  // Add education entry
  addEducation() {
    this.education.push(this.fb.group({
      degree: ['', [Validators.maxLength(100)]],
      university: ['', [Validators.maxLength(120)]],
      passing_year: [new Date().getFullYear(), [Validators.min(1900)]]
    }));
  }

  // Remove education entry
  removeEducation(index: number) {
    this.education.removeAt(index);
  }

  // Add certification
  addCertification() {
    this.certifications.push(this.fb.group({
      certification_name: ['',[Validators.maxLength(120)]],
      issuing_authority: ['',[Validators.maxLength(120)]],
      year_of_issue: [new Date().getFullYear(), [Validators.min(1900)]]
    }));
  }

  // Remove certification
  removeCertification(index: number) {
    this.certifications.removeAt(index);
  }

  // Add award
  addAward() {
    this.awards.push(this.fb.group({
      award_name: ['', [Validators.required, Validators.maxLength(120)]],
      purpose: ['', [Validators.maxLength(150)]],
      year_of_award: [new Date().getFullYear(), [Validators.min(1900)]]
    }));
  }

  // Remove award
  removeAward(index: number) {
    this.awards.removeAt(index);
  }

  // Toggle working day
  toggleWorkingDay(dayIndex: number) {
    // console.log('Toggling working day for index:', dayIndex);
    const day = this.weeklySchedule.at(dayIndex) as FormGroup;
    const isWorking = day.get('is_working_day')?.value;
    day.patchValue({ is_working_day: !isWorking });
    
    const slotsArray = day.get('work_slots') as FormArray;
    if (!isWorking && slotsArray.length === 0) {
      slotsArray.push(this.createWorkSlot());
    }
  }

  // Add work slot to a day
  addWorkSlot(dayIndex: number) {
    const day = this.weeklySchedule.at(dayIndex) as FormGroup;
    const slotsArray = day.get('work_slots') as FormArray;
    slotsArray.push(this.createWorkSlot());
  }

  // Remove work slot
  removeWorkSlot(dayIndex: number, slotIndex: number) {
    const day = this.weeklySchedule.at(dayIndex) as FormGroup;
    const slotsArray = day.get('work_slots') as FormArray;
    slotsArray.removeAt(slotIndex);
  }

  // Get work slots for a day
  getWorkSlots(dayIndex: number): FormArray {
    const day = this.weeklySchedule.at(dayIndex) as FormGroup;
    return day.get('work_slots') as FormArray;
  }

  // Copy schedule to all working days
  // copyScheduleToAll(dayIndex: number) {
  //   const sourceDay = this.weeklySchedule.at(dayIndex) as FormGroup;
  //   const sourceSlots = (sourceDay.get('work_slots') as FormArray).value;
    
  //   this.weeklySchedule.controls.forEach((day: AbstractControl, index) => {
  //     if (index !== dayIndex) {
  //       const dayGroup = day as FormGroup;
  //       const isWorking = dayGroup.get('is_working_day')?.value;
        
  //       if (isWorking) {
  //         const slotsArray = dayGroup.get('work_slots') as FormArray;
  //         slotsArray.clear();
  //         sourceSlots.forEach((slot: any) => {
  //           slotsArray.push(this.createWorkSlot(slot.start_time, slot.end_time));
  //         });
  //       }
  //     }
  //   });
    
  //   this.toast.success('Schedule copied to all working days');
  // }

  setCommonWorkingHours() {
  this.weeklySchedule.controls.forEach((day: AbstractControl, index) => {
    const dayGroup = day as FormGroup;
    const dayOfWeek = dayGroup.get('day_of_week')?.value;
    
    if (dayOfWeek >= 0 && dayOfWeek <= 4) {
      dayGroup.patchValue({ is_working_day: true });
      const slotsArray = dayGroup.get('work_slots') as FormArray;
      slotsArray.clear();
      slotsArray.push(this.createWorkSlot('09:00', '17:00'));
    } else {
      dayGroup.patchValue({ is_working_day: false });
      const slotsArray = dayGroup.get('work_slots') as FormArray;
      slotsArray.clear();
    }
  });
  
  this.toast.success('Set to Mon-Fri 9:00 AM - 5:00 PM');
}
  // Clear all schedules
  clearAllSchedules() {
    this.weeklySchedule.controls.forEach((day: AbstractControl) => {
      const dayGroup = day as FormGroup;
      dayGroup.patchValue({ is_working_day: false });
      const slotsArray = dayGroup.get('work_slots') as FormArray;
      slotsArray.clear();
    });
    
    this.toast.info('All schedules cleared');
  }

  formatDateToYMD(date: string | Date): string {
  const d = new Date(date);
  const month = ('0' + (d.getMonth() + 1)).slice(-2);
  const day = ('0' + d.getDate()).slice(-2);
  return `${d.getFullYear()}-${month}-${day}`;
}

  // Load countries
  loadCountries() {
    this.httpservice.sendGetRequest(URLUtils.getCountries).subscribe(
      (res: any) => {
        this.countries = res.data.countries;
        this.countries.shift();
      }
    );
  }

  getData() {
    this.spinnerService.show();
    this.httpservice.sendGetRequest(URLUtils.extendedProfile).subscribe(
      (res: any) => {
        this.spinnerService.hide();
        console.log('----API Response:', res); // Debug log
        
        if (res.error === false && res.data && res.data.profile) {
          // Extract the profile from the nested structure
          const profileData = res.data.profile;
          this.profile_details = profileData;
          this.basic_profile = profileData;
          this.firm_details = profileData.firm;
          this.subscription_details = profileData.subscription;
          // For Firm Profile view (SU) and AAM, completion data lives under firm.profile_completion;
          // for all other views use the top-level profile_completion_percentage.
          this.profileCompletionPercentage = (this.isFirmProfileView || this.isAAM)
            ? (profileData.firm?.profile_completion?.completion_percentage ?? 0)
            : (profileData.profile_completion_percentage ?? 0);
          this.showProfileBanner = (this.isFirmProfileView || this.isAAM)
            ? (profileData.firm?.show_profile_banner === true)
            : (profileData.show_profile_banner === true);
          
          // Profile picture is loaded once in ngOnInit via _loadProfilePicForCurrentView().
          // When getData() is called after a pic upload/remove, update profilePictureUrl
          // directly from the profile response — the signed S3 URL is the source of truth.
          const picUrl = (this.isFirmProfileView || this.isAAM)
            ? (profileData.firm?.profile_pic_url || null)
            : (profileData.profile_pic_url || null);
          this.profilePictureUrl = picUrl || null;
          // Only sync the topbar avatar when the displayed pic IS the logged-in
          // user's personal photo.  For SU on "Firm Profile" view the card shows
          // the firm logo — the topbar must keep showing the SU's personal pic,
          // so we intentionally skip setProfilePic() here.
          // AAM always uses the firm logo as their identity pic, so they sync.
          if (!this.isFirmProfileView) {
            this.httpservice.setProfilePic(picUrl || null);
          }

          this.loadFormData();
          // console.log('Processed data:', {
          //   profile: this.profile_details,
          //   firm: this.firm_details,
          //   subscription: this.subscription_details,
          //   completion: this.profileCompletionPercentage
          // }); // Debug log
          
          this.populateAllForms();
             this.resetBioExpansion();
          this._captureSnapshots();
        } else {
          this.toast.error('Failed to load profile data');
        }
      },
      (error) => {
        this.spinnerService.hide();
        this.handleError(error);
      }
    );
  }

  dismissProfileBanner() {
    const payload = (this.isFirmProfileView || this.isAAM)
      ? { firm: { profile_completion_banner_dismissed: true } }
      : { profile_completion_banner_dismissed: true };

    this.httpservice.sendPatchRequest(URLUtils.extendedProfile, payload).subscribe(
      (res: any) => {
        if (res.error === false || res.msg) {
          this.showProfileBanner = false;
        } else {
          this.toast.error(res.msg || 'Failed to dismiss banner');
        }
      },
      (error: HttpErrorResponse) => {
        this.handleApiErrors(error);
      }
    );
  }

  // Populate all forms with data
  populateAllForms() {
    if (!this.basic_profile) {
      console.warn('No profile data to populate');
      return;
    }
    const nameFieldValue = this.basic_profile?.name || '';

    this.personalInfoForm.patchValue({
      fullname: this.firm_details?.fullname || '',
      country: this.firm_details?.address?.country || '',
      contact_person: (this.isFirmProfileView || this.isAAM) ? (this.firm_details?.contact_person || '') : nameFieldValue,
      nationality: this.basic_profile?.nationality || 'Indian',
      email: (this.isFirmProfileView || this.isAAM)
        ? (this.firm_details?.email || '')
        : (this.basic_profile?.email || (this.isSoloCategory ? (localStorage.getItem('user_email') || '') : '')),
      billing_currency: this.firm_details?.billing_currency || 'IndianRupee(INR)',
      mobile: (this.isFirmProfileView || this.isAAM) ? (this.firm_details?.contact_phone || '') : (this.basic_profile?.mobile || localStorage.getItem('user_mobile') || ''),
      date_of_birth: this.basic_profile?.date_of_birth || '',
      website: this.firm_details?.website || '',
      gender: (this.basic_profile?.gender || '').toLowerCase(),
      bio_description: (this.isFirmProfileView || this.isAAM) ? (this.firm_details?.firm_description || '') : (this.basic_profile?.bio_description || ''),
      consultation_fee: this.basic_profile?.consultation_fee?.amount || ''
    });
    this.applyIdentifierFieldDisable();

    const prefilledPracticeAreas = (this.basic_profile?.practice_areas && this.basic_profile.practice_areas.length > 0)
    ? this.basic_profile.practice_areas
    : (this.firm_details?.practice_areas || []);
    

    // Practice details — for SU Firm Profile view and AAM, read firm-level fields from firm_details
    const isFirmContext = this.isFirmProfileView || this.isAAM;
    this.practiceDetailsForm.patchValue({
      bar_council_id: isFirmContext ? (this.firm_details?.reg_id || '') : (this.basic_profile?.bar_council_id || ''),
      years_of_experience: isFirmContext ? (this.firm_details?.years_of_incorporation ?? null) : (this.basic_profile?.years_of_experience ?? null),
      practice_areas: isFirmContext ? (this.firm_details?.practice_areas || []) : prefilledPracticeAreas,
      services_offered: isFirmContext ? (this.firm_details?.services_offered || []) : (this.basic_profile?.services_offered || []),
      languages_spoken: this.basic_profile?.languages_spoken || [],
      cases_handled: this.basic_profile?.cases_handled || []
    });

    // Populate court_enrollments FormArray
    this.courtEnrollments.clear();
    this.courtTypeSearch = [];
    this.courtStateSearch = [];
    this.courtCitySearch = [];
    this.showCityOtherInput = [];
    this.courtCityOtherInput = [];
    if (!isFirmContext && Array.isArray(this.basic_profile?.court_enrollments)) {
      this.basic_profile.court_enrollments.forEach((c: any, idx: number) => {
        this.courtEnrollments.push(this._buildCourtRow(c));
        this.courtTypeSearch[idx] = '';
        this.courtStateSearch[idx] = '';
        this.courtCitySearch[idx] = '';
        this.showCityOtherInput[idx] = false;
        this.courtCityOtherInput[idx] = '';
      });
    }

    // Addresses
    if (this.firm_details?.address) {
      this.practiceDetailsForm.get('address')?.patchValue({
        house_flat_no: this.firm_details.address.house_flat_no || '',
        street: this.firm_details.address.street || '',
        city_town: this.firm_details.address.city_town || '',
        state: this.firm_details.address.state || '',
        country: this.firm_details.address.country || '',
        zipcode: this.firm_details.address.zipcode || ''
      });
    }

    if (this.firm_details?.correspondence_address) {
      this.practiceDetailsForm.get('correspondence_address')?.patchValue({
        house_flat_no: this.firm_details.correspondence_address.house_flat_no || '',
        street: this.firm_details.correspondence_address.street || '',
        city_town: this.firm_details.correspondence_address.city_town || '',
        state: this.firm_details.correspondence_address.state || '',
        country: this.firm_details.correspondence_address.country || '',
        zipcode: this.firm_details.correspondence_address.zipcode || ''
      });
    }
    // Auto-detect if correspondence == registered
    const reg = this.practiceDetailsForm.get('address')?.value;
    const corr = this.practiceDetailsForm.get('correspondence_address')?.value;

    const isSame =
      reg &&
      corr &&
      JSON.stringify(reg) === JSON.stringify(corr);

    this.practiceDetailsForm.patchValue(
      { sameAsRegistered: isSame },
      { emitEvent: true }
    );
    this.education.clear();
    if (this.basic_profile?.education && Array.isArray(this.basic_profile.education) && this.basic_profile.education.length > 0) {
      this.basic_profile.education.forEach((edu: any) => {
        this.education.push(this.fb.group({
          degree: [edu.degree],
          university: [edu.university],
          passing_year: [edu.passing_year]
        }));
      });
    }
    this.certifications.clear();
    // Certifications
    if (this.basic_profile?.certifications && Array.isArray(this.basic_profile.certifications) && this.basic_profile.certifications.length > 0) {
      this.basic_profile.certifications.forEach((cert: any) => {
        this.certifications.push(this.fb.group({
          certification_name: [cert.certification_name],
          issuing_authority: [cert.issuing_authority],
          year_of_issue: [cert.year_of_issue || new Date().getFullYear()]
        }));
      });
    }
    this.awards.clear();
    // Awards — for SU Firm Profile view and AAM, read from firm_details; otherwise from basic_profile
    const awardsSource = (this.isFirmProfileView || this.isAAM)
      ? (this.firm_details?.awards || [])
      : (this.basic_profile?.awards || []);
    if (Array.isArray(awardsSource) && awardsSource.length > 0) {
      awardsSource.forEach((award: any) => {
        this.awards.push(this.fb.group({
          award_name: [award.award_name, [Validators.required, Validators.maxLength(120)]],
          purpose: [award.purpose || ''],
          year_of_award: [award.year_of_award || new Date().getFullYear()]
        }));
      });
    }

    // Availability
if (this.basic_profile?.availability) {
  const avail = this.basic_profile.availability;

  this.availabilityForm.patchValue({
    timezone: avail.timezone || 'Asia/Kolkata',
    // slot_duration: 30,
    // buffer_time: avail.buffer_time || 0,
    // advance_booking_window_hours: 4,
    // cancellation_window_hours: 2,
    // reschedule_window_hours: 2
  });
  if (Array.isArray(avail.weekly_schedule)) {
  
    this.weeklySchedule.clear();
    avail.weekly_schedule.forEach((apiDay: any) => {
      const dayGroup = this.createDaySchedule(
        apiDay.day_of_week,
        apiDay.day_name || '',
        apiDay.is_working_day || false,
        apiDay.date || ''
      );

     
      dayGroup.patchValue({
        date_label: apiDay.date_label || apiDay.day_name || '',
        expert_slots: apiDay.expert_slots || []
      });

      // Populate work slots
      const slotsArray = dayGroup.get('work_slots') as FormArray;
      slotsArray.clear();

      if (apiDay.is_working_day && Array.isArray(apiDay.work_slots)) {
        apiDay.work_slots.forEach((slot: any) => {
          slotsArray.push(
            this.createWorkSlot(slot.start_time, slot.end_time)
          );
        });
      }

      this.weeklySchedule.push(dayGroup);
    });
  }
}
  }
  private resetBioExpansion(): void {
  this.isBioExpanded = false;
}

getEndTimeOptions(startTime: string): string[] {
  if (!startTime) return this.timeOptions;

  const startIndex = this.timeOptions.indexOf(startTime);
  if (startIndex === -1) return this.timeOptions;
  
  // Return all times after the start time
  return this.timeOptions.slice(startIndex + 1);
}

formatFirstLetterUppercase(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : text;
}

getSlotRanges(slots: any[] | null | undefined): string[] {
  if (!slots || !Array.isArray(slots)) {
    return [];
  }

  return slots.map(slot => `${slot.start_time}-${slot.end_time}`);
}
shouldShowReadMore(): boolean {
  const bioText = (this.isFirmProfileView || this.isAAM)
    ? this.firm_details?.firm_description
    : this.basic_profile?.bio_description;
  
  if (!bioText) {
    return false;
  }
  
  // Show read more if text length exceeds threshold
  return bioText.length > this.BIO_CHAR_THRESHOLD;
}
toggleBio(): void {
  this.isBioExpanded = !this.isBioExpanded;
}
  // Load profile picture - REMOVED (already loaded in getData)
  loadProfilePicture() {
    if (!this.profilePictureUrl) {
      if (this.isFirmProfileView) {
        // SU Firm Profile — show firm logo, do NOT update topbar
        this._loadAndSetFirmLogo(false);
      } else if (this.isAAM) {
        // AAM — firm logo is their profile pic; sync topbar
        this._loadAndSetFirmLogo(true);
      } else {
        this.httpservice.sendGetRequest(URLUtils.extendedProfilePic).subscribe(
          (img: any) => {
            this.profilePictureUrl = img?.profilepic_url || null;
          },
          () => {
            this.profilePictureUrl = null;
          }
        );
      }
    }
  }

  /**
   * Called once from ngOnInit (inside queryParams.subscribe) after
   * isFirmProfileView is resolved.  Decides which API to use:
   *
   *  • SU on "Firm Profile" menu  → GET /v3/firm/profile/pic  (no topbar update)
   *  • AAM (any view)             → GET /v3/firm/profile/pic  (syncs topbar)
   *  • SU on "My Profile" menu    → GET extendedProfilePic (syncs topbar)
   *  • All other roles (TM/GH/Solo etc.) → GET extendedProfilePic (syncs topbar)
   */
  private _loadProfilePicForCurrentView(): void {
    if (this.isFirmProfileView) {
      // SU clicked "Firm Profile" — show firm logo in card, keep topbar as personal pic
      this._loadAndSetFirmLogo(false);
    } else if (this.isAAM) {
      // AAM — firm logo IS their profile pic; sync topbar too
      this._loadAndSetFirmLogo(true);
    } else {
      this.spinnerService.show();
      // SU "My Profile", TM, GH, Solo and all other roles — use personal profile pic
      this.httpservice.sendGetRequest(URLUtils.extendedProfilePic).subscribe(
        (res: any) => {
          this.spinnerService.hide();
          const url = res?.data?.profilepic_url || res?.profilepic_url || res?.data?.imageUrl || null;
          this.profilePictureUrl = url;
          this.httpservice.setProfilePic(url);
        },
        () => {
          this.profilePictureUrl = null;
        }
      );
    }
  }

  /**
   * Fetches the firm logo from /v3/firm/profile/pic and updates profilePictureUrl.
   * Used by SU (Firm Profile view) and AAM after upload and on initial load.
   *
   * @param syncTopbar  Pass true only when this pic should also update the
   *                    dashboard topbar (i.e. AAM, whose firm logo IS their
   *                    profile pic).  For SU on Firm Profile pass false —
   *                    the topbar must keep showing the SU's personal pic.
   */
  private _loadAndSetFirmLogo(syncTopbar: boolean = false): void {
    console.log('Loading firm logo with syncTopbar =', syncTopbar);
    this.spinnerService.show();
    this.httpservice.sendGetRequest(URLUtils.firmlogo).subscribe(
      (res: any) => {
        this.spinnerService.hide();
        const url = res?.data?.url || res?.data?.imageUrl || res?.imageUrl || null;
        this.profilePictureUrl = url;
        if (syncTopbar) {
          this.httpservice.setProfilePic(url);
        }
      },
      () => {
        this.profilePictureUrl = null;
      }
    );
  }

  get adjustTransform(): string {
  return `translate(${this.adjustX}px, ${this.adjustY}px) scale(${this.adjustScale}) rotate(${this.adjustRotation}deg)`;
}

// ── Zoom via slider ───────────────────────────────────────────────────────────
onZoomChange(val: number) {
  this.adjustScale = Math.min(3, Math.max(1, val));
}

// ── Zoom via scroll wheel ─────────────────────────────────────────────────────
onWheel(event: WheelEvent) {
  event.preventDefault();
  this.adjustScale = Math.min(3, Math.max(1, this.adjustScale - event.deltaY * 0.001));
}

// ── Rotate ────────────────────────────────────────────────────────────────────
rotateCW() {
  this.adjustRotation += 90;
}

rotateCCW() {
  this.adjustRotation -= 90;
}

// ── Drag (mouse) ──────────────────────────────────────────────────────────────
startDrag(event: MouseEvent) {
  this._dragging = true;
  this._dragStartX = event.clientX;
  this._dragStartY = event.clientY;
  this._dragOriginX = this.adjustX;
  this._dragOriginY = this.adjustY;
  event.preventDefault();
}

onDrag(event: MouseEvent) {
  if (!this._dragging) return;
  this.adjustX = this._dragOriginX + (event.clientX - this._dragStartX);
  this.adjustY = this._dragOriginY + (event.clientY - this._dragStartY);
}

stopDrag() {
  this._dragging = false;
}

// ── Drag (touch) ──────────────────────────────────────────────────────────────
startDragTouch(event: TouchEvent) {
  if (event.touches.length !== 1) return;
  const t = event.touches[0];
  this._dragging = true;
  this._dragStartX = t.clientX;
  this._dragStartY = t.clientY;
  this._dragOriginX = this.adjustX;
  this._dragOriginY = this.adjustY;
}

onDragTouch(event: TouchEvent) {
  if (!this._dragging || event.touches.length !== 1) return;
  const t = event.touches[0];
  this.adjustX = this._dragOriginX + (t.clientX - this._dragStartX);
  this.adjustY = this._dragOriginY + (t.clientY - this._dragStartY);
}

// ── Open adjuster (saves current state for cancel restore) ───────────────────
startAdjust() {
  this._savedAdjustX = this.adjustX;
  this._savedAdjustY = this.adjustY;
  this._savedAdjustScale = this.adjustScale;
  this._savedAdjustRotation = this.adjustRotation;
  this.isAdjustingPic = true;
}

// ── Reset position/zoom/rotation to defaults ──────────────────────────────────
resetAdjust() {
  this.adjustX = 0;
  this.adjustY = 0;
  this.adjustScale = 1;
  this.adjustRotation = 0;
}

// ── Save: fire upload if a new file is pending, otherwise just close ──────────
saveAdjust() {
  this.isAdjustingPic = false;

  if (!this._pendingUploadFile) return;

  const file = this._pendingUploadFile;
  this._pendingUploadFile = null;

  const img = new Image();
  const blobUrl = URL.createObjectURL(file);
  img.src = blobUrl;

  img.onload = () => {
    URL.revokeObjectURL(blobUrl);

    const SIZE = 300; // matches both .profile-image-view and .wa-crop-wrap

    // Replicate object-fit: contain — fit entire image inside SIZE x SIZE
    const scale = Math.min(SIZE / img.naturalWidth, SIZE / img.naturalHeight);
    const drawW = img.naturalWidth  * scale;
    const drawH = img.naturalHeight * scale;

    const canvas = document.createElement('canvas');
    canvas.width  = SIZE;
    canvas.height = SIZE;

    const ctx = canvas.getContext('2d')!;
    ctx.save();
    ctx.translate(SIZE / 2 + this.adjustX, SIZE / 2 + this.adjustY);
    ctx.rotate((this.adjustRotation * Math.PI) / 180);
    ctx.scale(this.adjustScale, this.adjustScale);
    ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();

    canvas.toBlob((blob) => {
      if (!blob) { this.toast.error('Failed to process image'); return; }
      const adjustedFile = new File([blob], file.name, { type: 'image/jpeg' });
      this._uploadFile(adjustedFile);
    }, 'image/jpeg', 0.92);
  };
}

// ── Cancel: restore saved state and revert preview if upload was pending ──────
cancelAdjust() {
  const hadPending = !!this._pendingUploadFile;

  this.adjustX = this._savedAdjustX;
  this.adjustY = this._savedAdjustY;
  this.adjustScale = this._savedAdjustScale;
  this.adjustRotation = this._savedAdjustRotation;
  this.isAdjustingPic = false;
  //this.adjustPreviewUrl = null;
  this._pendingUploadFile = null;

  if (hadPending) {
    // Revert the blob preview URL back to whatever is on the server
    this._loadProfilePicForCurrentView();
  }
}

  // Profile picture operations
onProfilePictureSelect(event: any) {
  const file: File = event.target.files?.[0];
  
  // Reset input immediately so the same file can be re-selected later
  event.target.value = '';
  
  if (!file) return;

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  if (!allowedTypes.includes(file.type)) {
    this.toast.error('Only JPG, PNG, or WEBP images are allowed');
    return;
  }

  const MIN_FILE_SIZE = 30; // 30 KB
  const minSize = MIN_FILE_SIZE * 1024;
  const MAX_SIZE_MB = 5;
  const maxSize = MAX_SIZE_MB * 1024 * 1024;

  // if (file.size < minSize) {
  //   this.toast.error(`Profile picture must be greater than ${MIN_FILE_SIZE}KB`);
  //   return;
  // }

  // if (file.size > maxSize) {
  //   this.toast.error(`Profile picture must be less than ${MAX_SIZE_MB}MB`);
  //   return;
  // }

   // Show local preview immediately — but ONLY inside the modal, never on
   // the main profile page, until the upload actually succeeds.
  let blobUrl = URL.createObjectURL(file);
  this.modalPreviewUrl = this.sanitizer.bypassSecurityTrustUrl(blobUrl) as any;


  // Hold the file — upload fires only when user taps Save
  this._pendingUploadFile = file;

  // Reset any previous adjust state and go straight into crop/reposition —
  // picking a file already signals intent to proceed, so don't make the
  // user click an extra "Continue" button.
  this.resetAdjust();
  this.startAdjust();
  this.isEditProfilePic = true;
  this.isPhotoModalOpen = true;
  this.photoModalStep = 'crop';

    // Optimistic preview — show chosen image immediately in the card.
    // For SU on Firm Profile do NOT push the blob URL to the topbar (topbar
    // must keep showing the SU's personal pic, not the firm logo blob).
  //   this.profilePictureUrl = URL.createObjectURL(file);
  //   if (!this.isFirmProfileView) {
  //     this.httpservice.setProfilePic(this.profilePictureUrl);
  //   }

  // const formData = new FormData();
  // formData.append('file', file);

  // this.spinnerService.show();

    // Determine endpoints based on role / view:
    //   SU Firm Profile → firm logo endpoints
    //   AAM             → firm logo endpoints
    //   SU My Profile / others → standard profile pic endpoints
    // const isFirmLogoContext = this.isFirmProfileView || this.isAAM;
    // const uploadUrl = isFirmLogoContext
    //   ? URLUtils.firmLogoUpload
    //   : URLUtils.extendedProfilePicUpload;

    // this.httpservice.sendPostRequest(uploadUrl, formData)
    //   .subscribe(
    //     (res: any) => {
    //       this.spinnerService.hide();
    //       if (res.error === false) {
    //         this.toast.success('Profile picture uploaded');
    //         this.isEditProfilePic = false;
    //         // Reload the confirmed server URL.
    //         // syncTopbar = false for SU Firm Profile (topbar = personal pic);
    //         // syncTopbar = true  for AAM (firm logo IS their profile pic).
    //         if (this.isFirmProfileView) {
    //           this._loadAndSetFirmLogo(false);
    //         } else if (this.isAAM) {
    //           this._loadAndSetFirmLogo(true);
    //         } else {
    //           this.spinnerService.show();
    //           this.httpservice.sendGetRequest(URLUtils.extendedProfilePic).subscribe((picRes: any) => {
    //             this.spinnerService.hide();
    //             console.log('Reloaded profile pic response:', picRes);
    //             if (picRes.error == false) {
    //               this.profilePictureUrl = picRes.data.imageUrl;
    //               this.httpservice.setProfilePic(this.profilePictureUrl);
    //             }
    //           });
    //         }
    //       } else {
    //         this.toast.error(res.msg || 'Upload failed');
    //       }
    //     },
    //     () => {
    //       this.spinnerService.hide();
    //       this.toast.error('Upload failed');
    //     }
    //   );
  }

  // ── Actual HTTP upload (only called from saveAdjust) ─────────────────────────
private _uploadFile(file: File) {
  const localPreview = URL.createObjectURL(file);
  this.profilePictureUrl = localPreview;
  if (!this.isFirmProfileView) {
    this.httpservice.setProfilePic(localPreview);
  }
  const formData = new FormData();
  formData.append('file', file);

  this.spinnerService.show();

  const isFirmLogoContext = this.isFirmProfileView || this.isAAM;
  const uploadUrl = isFirmLogoContext
    ? URLUtils.firmLogoUpload
    : URLUtils.extendedProfilePicUpload;

  this.httpservice.sendPostRequest(uploadUrl, formData).subscribe(
    (res: any) => {
      this.spinnerService.hide();
      if (res.error === false) {
        this.toast.success('Profile photo updated successfully.');
        this.isEditProfilePic = false;
        this.isPhotoModalOpen = false;
        this.photoModalStep = 'select';
        this.modalPreviewUrl = null;

        // Reload full profile (refreshes profile_pic_url, profile_completion_percentage, etc.)
        this.getData();
      } else {
        this.toast.error(res.msg || 'Upload failed');
        // Nothing left to retry (the pending file was already consumed) —
        // close the modal and revert the main page back to the saved photo.
        this.isEditProfilePic = false;
        this.isPhotoModalOpen = false;
        this.photoModalStep = 'select';
        this.modalPreviewUrl = null;
        this._loadProfilePicForCurrentView();
      }
    },
    () => {
      this.spinnerService.hide();
      this.toast.error('Upload failed');
      this.isEditProfilePic = false;
      this.isPhotoModalOpen = false;
      this.photoModalStep = 'select';
      this.modalPreviewUrl = null;
      this._loadProfilePicForCurrentView();
    }
  );
}

  triggerFileInput() {
    const fileInput = document.getElementById('profilePictureInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

removeProfilePicture() {
  const isFirmLogoContext = this.isFirmProfileView || this.isAAM;
    const deleteUrl = isFirmLogoContext
      ? URLUtils.firmlogo
      : URLUtils.extendedProfilePic;
  this.httpservice.sendDeleteRequest(deleteUrl).subscribe(
    (res: any) => {
      if (res.error === false) {
        this.toast.success('Profile Picture Removed!');
        this.profilePictureUrl = null;
        // Only clear the topbar avatar when removing a personal profile pic.
        // For SU on Firm Profile, the topbar keeps the SU's personal pic — don't touch it.
        if (!this.isFirmProfileView) {
          this.httpservice.setProfilePic(null);
        }
        this.isEditProfilePic = false;
        this.isPhotoModalOpen = false;
        this.photoModalStep = 'select';
        this.modalPreviewUrl = null;
        this.resetAdjust();

        // Reset the file input so the same file can be uploaded again after removal
        const fileInput = document.getElementById('profilePictureInput') as HTMLInputElement;
        if (fileInput) fileInput.value = '';

        // Reload full profile (refreshes profile_completion_percentage, etc.)
        this.getData();
      }
    }
  );
}


  getUserInitial(): string {
    const name = this.basic_profile?.name || localStorage.getItem('name');
    if (!name) return '';
    return name.trim()[0].toUpperCase();
  }

  getFirmInitial(): string {
    const firmName = this.firm_details?.fullname;
    if (!firmName) return '';
    return firmName.trim()[0].toUpperCase();
  }

  getemail(): string {
    const raw = localStorage.getItem('user_email') || '';
    try {
      // user_email is stored via JSON.stringify, so parse it to remove surrounding quotes
      const parsed = JSON.parse(raw);
      return typeof parsed === 'string' ? parsed : raw;
    } catch {
      return raw;
    }
  }

  /** Returns the identifier the user logged in with (email or mobile number). */
  getLoginIdentifier(): string {
    return this.loggedInIdentifier;
  }

  // Form submission methods
  saveProfile() {
    // if (!ensureActiveSubscription(this.confirmationDialogService)) return;

    this.submitted = true;

    let isValid = false;
    let payload: any = {};

    switch (this.activeTab) {
      case 'practice':
        isValid = this.personalInfoForm.valid && this.practiceDetailsForm.valid;
        if (isValid) {
          const personalValue = this.personalInfoForm.value;
          const practiceValue = this.practiceDetailsForm.getRawValue();           
          // console.log('personalValue', personalValue)
          // console.log('practiceValue', personalValue)
          
          payload = {
            name: personalValue.contact_person,
            email: this.getemail(),
            mobile: personalValue.mobile,
            date_of_birth: this.formatDateToYMD(personalValue.date_of_birth),
            gender: personalValue.gender,
            nationality: personalValue.nationality || 'indian',
            bio_description: personalValue.bio_description,
            bar_council_id: practiceValue.bar_council_id,
            years_of_experience: practiceValue.years_of_experience,
             practice_areas: Array.isArray(practiceValue.practice_areas) 
            ? practiceValue.practice_areas 
            : [],
          services_offered: Array.isArray(practiceValue.services_offered) 
            ? practiceValue.services_offered 
            : [],
          languages_spoken: Array.isArray(practiceValue.languages_spoken) 
            ? practiceValue.languages_spoken 
            : [],
            firm: {
              fullname: personalValue.fullname,
              // contact_person keeps its original value — the editable field is "name" (top-level)
              contact_person: this.firm_details?.contact_person || '',
              email: this.getemail(),
              billing_currency: personalValue.billing_currency,
              website: personalValue.website,
              address: practiceValue.address,
              correspondence_address: practiceValue.correspondence_address
            }
          };
          // console.log('paylaod',payload)
        } else {
          this.toast.error('Please fill all required fields in Personal Information and Practice Details');
          return;
        }
        break;

      case 'education':
        isValid = this.educationForm.valid;
        if (isValid) {
          payload = {
            education: this.education.value,
            certifications: this.certifications.value,
            awards: this.awards.value
          };
        }
        break;

      case 'availability':
        isValid = this.availabilityForm.valid;
        if (isValid) {
          const validationResult = this.validateAvailability();
          if (!validationResult.valid) {
            this.toast.error(validationResult.message);
            return;
          }

          const availabilityValue = this.availabilityForm.value;

          payload = {
            availability: {
              ...availabilityValue,
              weekly_schedule: availabilityValue.weekly_schedule.filter(
                (day: any) => day.is_working_day === true
              )
            }
          };

        }
        break;
    }

    if (!isValid) {
      this.toast.error('Please fill all required fields correctly');
      return;
    }

    this.spinnerService.show();

    this.httpservice.sendPatchRequest(URLUtils.extendedProfile, payload).subscribe(
      (res: any) => {
        this.spinnerService.hide();
        if (res.msg || res.error === false) {
          this.toast.success(res.msg || 'Profile updated successfully');
          this.isEdit = false;
          this.submitted = false;
          this.getData();
        }
      },
      (error: HttpErrorResponse) => {
        this.spinnerService.hide();
        this.handleApiErrors(error);
      }
    );
  }
  // Validate availability settings
  validateAvailability(): { valid: boolean; message: string } {
    const schedule = this.availabilityForm.get('weekly_schedule')?.value as DaySchedule[];

    for (const day of schedule) {
      if (!day.is_working_day) continue;

      if (!day.work_slots || day.work_slots.length === 0) {
        return {
          valid: false,
          message: `${day.date_label || day.day_name} is marked as working day but has no time slots`
        };
      }

      for (let i = 0; i < day.work_slots.length; i++) {
        const slot = day.work_slots[i];

        if (slot.start_time >= slot.end_time) {
          return {
            valid: false,
            message: `${day.date_label || day.day_name} Slot #${i + 1}: start time must be before end time`
          };
        }

        for (let j = i + 1; j < day.work_slots.length; j++) {
          const otherSlot = day.work_slots[j];
          if (
            (slot.start_time < otherSlot.end_time && slot.end_time > otherSlot.start_time) ||
            (otherSlot.start_time < slot.end_time && otherSlot.end_time > slot.start_time)
          ) {
            return {
              valid: false,
              message: `${day.date_label || day.day_name} has overlapping work slots`
            };
          }
        }
      }
    }

    return { valid: true, message: '' };
  }

  // Error handling
  handleApiErrors(error: HttpErrorResponse) {
    if (error.status === 401 || error.status === 403) {
      this.toast.error(error.error.msg || 'Unauthorized');
      return;
    }

    if (error.error?.errors && Array.isArray(error.error.errors)) {
      error.error.errors.forEach((err: any) => {
        this.toast.error(`${err.field}: ${err.msg}`);
      });
    } else if (error.error?.msg) {
      this.toast.error(error.error.msg);
    } else {
      this.toast.error('An error occurred while updating profile');
    }
  }

  handleError(error: any) {
    console.error('Error:', error);
    this.toast.error(error?.error?.msg || 'An error occurred');
  }

  shouldShowError(group: AbstractControl | null, controlName: string): boolean {
  const control = group?.get(controlName);
  return this.submitted && !!control && control.invalid;
}


scrollToFirstInvalid() {
  const firstInvalidControl = document.querySelector('.ng-invalid') as HTMLElement | null;

  if (firstInvalidControl) {
    firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (firstInvalidControl as HTMLElement).focus();
  }
}

  isFormValid(): boolean {
    switch (this.activeTab) {
      case 'personal':
        return this.personalInfoForm.valid;
      case 'practice':
        return this.practiceDetailsForm.valid;
      case 'education':
        return this.educationForm.valid;
      case 'availability':
        return this.availabilityForm.valid;
      default:
        return false;
    }
  }

  // Navigation methods
  editMember() {
    // if (!ensureActiveSubscription(this.confirmationDialogService)) return;
    this.isEdit = true;
  }
  loadFormData() {
    // Personal Information
    // Always use basic_profile.name for the Name/Contact Name field (API key "name")
    const nameFieldValue = this.basic_profile?.name || '';
    this.personalInfoForm.patchValue({
      fullname: this.firm_details?.fullname || '',
      country: this.firm_details?.address?.country || 'India',
      contact_person: (this.isFirmProfileView || this.isAAM) ? (this.firm_details?.contact_person || '') : nameFieldValue,
      nationality: this.basic_profile?.nationality || 'indian',
      email: (this.isFirmProfileView || this.isAAM) ? (this.firm_details?.email || '') : (this.basic_profile?.email || ''),
      billing_currency: this.firm_details?.billing_currency || '',
      mobile: (this.isFirmProfileView || this.isAAM) ? (this.firm_details?.contact_phone || '') : (this.basic_profile?.mobile || localStorage.getItem('user_mobile') || ''),
      date_of_birth: this.basic_profile?.date_of_birth || '',
      website: this.firm_details?.website || '',
      gender: (this.basic_profile?.gender || '').toLowerCase(),
      bio_description: (this.isFirmProfileView || this.isAAM) ? (this.firm_details?.firm_description || '') : (this.basic_profile?.bio_description || ''),
      consultation_fee: this.basic_profile?.consultation_fee?.amount || ''
    });
    // Re-apply the locked field after patching to ensure disable state is preserved
    this.applyIdentifierFieldDisable();


    const prefilledPracticeAreas = (this.basic_profile?.practice_areas && this.basic_profile.practice_areas.length > 0)
    ? this.basic_profile.practice_areas
    : (this.firm_details?.practice_areas || []);

    

    // Practice Details — for AAM and Firm Profile view, read firm-level fields from firm_details
    const isFirmContext = this.isFirmProfileView || this.isAAM;
    this.practiceDetailsForm.patchValue({
      bar_council_id: isFirmContext ? (this.firm_details?.reg_id || '') : (this.basic_profile?.bar_council_id || ''),
      years_of_experience: isFirmContext ? (this.firm_details?.years_of_incorporation ?? null) : (this.basic_profile?.years_of_experience ?? null),
      practice_areas: isFirmContext ? (this.firm_details?.practice_areas || []) : prefilledPracticeAreas,
      services_offered: isFirmContext ? (this.firm_details?.services_offered || []) : (this.basic_profile?.services_offered || []),
      languages_spoken: this.basic_profile?.languages_spoken || []
    });

    // Address
    if (this.firm_details?.address) {
      this.practiceDetailsForm.patchValue({
        address: {
          house_flat_no: this.firm_details.address.house_flat_no || '',
          street: this.firm_details.address.street || '',
          city_town: this.firm_details.address.city_town || '',
          state: this.firm_details.address.state || '',
          country: this.firm_details.address.country || '',
          zipcode: this.firm_details.address.zipcode || ''
        }
      });
    }

    // Correspondence Address
    if (this.firm_details?.correspondence_address) {
      this.practiceDetailsForm.patchValue({
        correspondence_address: {
          house_flat_no: this.firm_details.correspondence_address.house_flat_no || '',
          street: this.firm_details.correspondence_address.street || '',
          city_town: this.firm_details.correspondence_address.city_town || '',
          state: this.firm_details.correspondence_address.state || '',
          country: this.firm_details.correspondence_address.country || '',
          zipcode: this.firm_details.correspondence_address.zipcode || ''
        }
      });
    }

    // Check if addresses are same
    const reg = this.practiceDetailsForm.get('address')?.value;
    const corr = this.practiceDetailsForm.get('correspondence_address')?.value;
    const isSame =
      reg &&
      corr &&
      JSON.stringify(reg) === JSON.stringify(corr);

    this.practiceDetailsForm.patchValue(
      { sameAsRegistered: isSame },
      { emitEvent: true }
    );
    this.education.clear();
    if (this.basic_profile?.education && Array.isArray(this.basic_profile.education) && this.basic_profile.education.length > 0) {
      this.basic_profile.education.forEach((edu: any) => {
        this.education.push(this.fb.group({
          degree: [edu.degree],
          university: [edu.university],
          passing_year: [edu.passing_year]
        }));
      });
    }
    this.certifications.clear();
    // Certifications - FIXED
    if (this.basic_profile?.certifications && Array.isArray(this.basic_profile.certifications) && this.basic_profile.certifications.length > 0) {
      this.basic_profile.certifications.forEach((cert: any) => {
        this.certifications.push(this.fb.group({
          certification_name: [cert.certification_name],
          issuing_authority: [cert.issuing_authority],
          year_of_issue: [cert.year_of_issue || new Date().getFullYear()]
        }));
      });
    }
    this.awards.clear();
    // Awards - FIXED
    if (this.basic_profile?.awards && Array.isArray(this.basic_profile.awards) && this.basic_profile.awards.length > 0) {
      this.basic_profile.awards.forEach((award: any) => {
        this.awards.push(this.fb.group({
          award_name: [award.award_name, [Validators.required, Validators.maxLength(120)]],
          purpose: [award.purpose || ''],
          year_of_award: [award.year_of_award || new Date().getFullYear()]
        }));
      });
    }

    // Availability - FIXED WITH EXPERT_SLOTS
if (this.basic_profile?.availability) {
  const avail = this.basic_profile.availability;

  this.availabilityForm.patchValue({
    timezone: avail.timezone || 'Asia/Kolkata',
    // slot_duration: 30,
    // buffer_time: avail.buffer_time || 0,
    // advance_booking_window_hours: 4,
    // cancellation_window_hours: 2,
    // reschedule_window_hours: 2
  });
  if (Array.isArray(avail.weekly_schedule)) {
    // Clear and rebuild in API response order (no sorting applied)
    this.weeklySchedule.clear();
    avail.weekly_schedule.forEach((apiDay: any) => {
      const dayGroup = this.createDaySchedule(
        apiDay.day_of_week,
        apiDay.day_name || '',
        apiDay.is_working_day || false,
        apiDay.date || ''
      );

      // Patch date_label and expert_slots
      dayGroup.patchValue({
        date_label: apiDay.date_label || apiDay.day_name || '',
        expert_slots: apiDay.expert_slots || []
      });

      // Populate work slots
      const slotsArray = dayGroup.get('work_slots') as FormArray;
      slotsArray.clear();

      if (apiDay.is_working_day && Array.isArray(apiDay.work_slots)) {
        apiDay.work_slots.forEach((slot: any) => {
          slotsArray.push(
            this.createWorkSlot(slot.start_time, slot.end_time)
          );
        });
      }

      this.weeklySchedule.push(dayGroup);
    });
  }
}
  }
  onReset() {
    this.submitted = false;
    this.isEdit = false;
      this.resetBioExpansion();
    this.getData();
  }

  onClick(value: string) {
    this.selectedMatter = value;
  }

  // Utility methods
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  private getProductFromStorage(): string {
    const storedProduct = localStorage.getItem('product');
    return storedProduct || 'lauditor';
  }
  filterPracticeAreas() {
  const term = (this.practiceAreaSearch || '').toLowerCase().trim();
  const selected =
    this.practiceDetailsForm.get('practice_areas')?.value || [];

  this.filteredPracticeAreas = this.caseTypes.filter(area =>
    area.toLowerCase().includes(term)
  );
}

@HostListener('document:click', ['$event'])
onOutsideClick(event: MouseEvent) {
  const target = event.target as HTMLElement;

  if (!target.closest('.practice-area-wrapper')) {
    this.showPracticeAreaDropdown = false;

     this.practiceAreaSearch = '';
    this.filteredPracticeAreas = [...this.caseTypes];
  }

  if (!target.closest('.service-offered-wrapper')) {
    this.showServiceDropdown = false;
    this.serviceInput = '';
    this.serviceSearchMode = false;
    this.servicesList = [];
    this.showAllServices = false;
    this._updateFilteredServices();
  }
   
  // Address autocomplete
  if (!target.closest('.autocomplete-wrapper')) {
    this.showRegisteredStateDropdown = false;
    this.showMailingStateDropdown = false;
    this.showRegisteredCityDropdown = false;
    this.showMailingCityDropdown = false;
  }

  // Gender custom dropdown
  if (!target.closest('.gender-select-wrapper')) {
    this.isGenderDropdownOpen = false;
  }
}



addPracticeAreaFromInput() {
  // const value = this.practiceAreaInput?.trim();
  // if (!value) return;

  // const control = this.practiceDetailsForm.get('practice_areas');
  // const current: string[] = control?.value || [];

  // if (!current.includes(value)) {
  //   control?.setValue([...current, value]);
  // }

  // this.practiceAreaInput = '';
  // this.showPracticeAreaDropdown = false;
  return;
}

addPracticeAreaFromDropdown(area: string) {
  const control = this.practiceDetailsForm.get('practice_areas');
  const current = control?.value || [];

  if (current.includes(area)) return;

  control?.setValue([...current, area]);

  this.practiceAreaInput = '';
  this.showPracticeAreaDropdown = false;

  // US-2 / US-7: Re-fetch suggestions for updated PA list
  this.fetchSuggestedServices();
  this.fetchCourtSuggestions();
}
  selectPracticeArea(area: string) {
    const control = this.practiceDetailsForm.get('practice_areas');
    const current: string[] = control?.value || [];

    if (current.includes(area)) {
      // unselect
      control?.setValue(current.filter(a => a !== area));
    } else {
      control?.setValue([...current, area]);
    }

    // US-2 / US-7: Invalidate cached suggestions and re-fetch immediately so
    // the services and court-type dropdowns always reflect the latest PAs.
    this.suggestedServices = [];
    this.hasSuggestedServices = false;
    this.serviceSearchMode = false;
    this._updateFilteredServices();
    // Re-fetch service suggestions based on updated practice areas
    if (!this.serviceSearchMode) {
      this.fetchSuggestedServices();
    }
    // Refresh court suggestions too
    this.fetchCourtSuggestions();


    // ✅ Reset Practice Area search
    this.practiceAreaSearch = '';
    this.filterPracticeAreas();
    this.filteredPracticeAreas = [...this.caseTypes];
  }

// Check if a specific time slot is excluded
isSlotExcluded(startTime: string, endTime: string, excludedSlots: any[]): boolean {
  if (!excludedSlots || excludedSlots.length === 0) return false;
  
  return excludedSlots.some(slot => 
    slot.start_time === startTime && slot.end_time === endTime
  );
}

// Get total number of available 30-minute slots (excluding excluded ones)
getTotalSlotsCount(dayIndex: number): number {
  const day = this.weeklySchedule.at(dayIndex) as FormGroup;
  const startTime = this.getWorkSlots(dayIndex).at(0)?.get('start_time')?.value;
  const endTime = this.getWorkSlots(dayIndex).at(0)?.get('end_time')?.value;
  
  if (!startTime || !endTime) return 0;
  
  const totalSlots = this.generateThirtyMinuteSlots(startTime, endTime);
  const excludedSlots = day.get('expert_slots')?.value || [];
  
  // Filter out excluded slots
  const availableSlots = totalSlots.filter(slot => 
    !excludedSlots.some((excluded: any) => 
      excluded.start_time === slot.start && excluded.end_time === slot.end
    )
  );
  
  return availableSlots.length;
}

removePracticeArea(area: string) {
  const currentAreas = this.practiceDetailsForm.get('practice_areas')?.value || [];
  this.practiceDetailsForm.patchValue({
    practice_areas: currentAreas.filter((a: string) => a !== area)
  });
  // US-2 / US-7: Invalidate cached suggestions and re-fetch with updated PA list
  this.suggestedServices = [];
  this.hasSuggestedServices = false;
  this.serviceSearchMode = false;
  this._updateFilteredServices();
  this.fetchSuggestedServices();
  this.fetchCourtSuggestions();
}
togglePracticeAreaDropdown() {
    this.showPracticeAreaDropdown = !this.showPracticeAreaDropdown;

   if (this.showPracticeAreaDropdown) {
   
    this.practiceAreaSearch = '';
    this.filteredPracticeAreas = [...this.caseTypes];
  }
}
onPracticeAreaBlur() {
  // Delay to allow dropdown click to register
  setTimeout(() => {
    this.showPracticeAreaDropdown = false;
  }, 200);
}

// Update filtered list when input changes
// onPracticeAreaInputChange() {
//     this.showPracticeAreaDropdown = true; // 👈 ADD THIS FIRST

//   const searchTerm = (this.practiceAreaInput || '').toLowerCase().trim();
//   const currentAreas =
//     this.practiceDetailsForm.get('practice_areas')?.value || [];

//   if (currentAreas.length >= this.MAX_PRACTICE_AREAS) {
//     this.filteredPracticeAreas = [];
//     this.showPracticeAreaDropdown = false;
//     return;
//   }

//   if (!searchTerm) {
//     this.filteredPracticeAreas = [...this.practiceAreasList];
//   } else {
//     this.filteredPracticeAreas = this.practiceAreasList.filter(area =>
//       area.toLowerCase().includes(searchTerm)
//     );
//   }

//   this.filteredPracticeAreas = this.filteredPracticeAreas.filter(
//     area => !currentAreas.includes(area)
//   );
//   // const searchTerm = this.practiceAreaInput.toLowerCase();
  
//   // if (!searchTerm) {
//   //   this.filteredPracticeAreas = [...this.practiceAreasList];
//   // } else {
//   //   this.filteredPracticeAreas = this.practiceAreasList.filter(area =>
//   //     area.toLowerCase().includes(searchTerm)
//   //   );
//   // }
  
//   // // Remove already selected items from dropdown
//   // const currentAreas = this.practiceDetailsForm.get('practice_areas')?.value || [];
//   // this.filteredPracticeAreas = this.filteredPracticeAreas.filter(
//   //   area => !currentAreas.includes(area)
//   // );
// }

// ==========================================
// SERVICES CHIP INPUT METHODS (API-driven)
// ==========================================

/**
 * Fetch suggested services from the API based on the currently selected practice areas.
 * Called when the services dropdown is opened (if not already in search mode).
 * Reads all_services → allPredefinedServices (View More + duplicate detection).
 * Reads service_suggestions → suggestedServices (top suggestions shown immediately).
 * If service_suggestions is empty, falls back to showing all_services directly.
 */
fetchSuggestedServices(): void {
  const practiceAreas: string[] = this.practiceDetailsForm.get('practice_areas')?.value || [];
  if (!practiceAreas.length) {
    // Edge case: no practice area selected.
    // If allPredefinedServices is already populated from a prior call, show it directly.
    // Otherwise trigger a fetch (with empty practice_areas) to get all_services from the API.
    if (this.allPredefinedServices.length) {
      this.suggestedServices = [...this.allPredefinedServices];
      this.hasSuggestedServices = false;
      this._updateFilteredServices();
    } else {
      this._fetchServicesFromAPI([]);
    }
    return;
  }

  this._fetchServicesFromAPI(practiceAreas);
}

/** Internal: calls /v3/services/suggest and updates suggestedServices + allPredefinedServices */
private _fetchServicesFromAPI(practiceAreas: string[]): void {
  this.isLoadingServices = true;
  this.httpservice.sendPostRequest('/v3/services/suggest', { practice_areas: practiceAreas })
    .subscribe({
      next: (res: any) => {
        this.isLoadingServices = false;
        if (res && !res.error && res.data) {
          // Populate the full predefined list from all_services
          if (Array.isArray(res.data.all_services) && res.data.all_services.length) {
            this.allPredefinedServices = res.data.all_services as string[];
          }
          // Use service_suggestions for top suggestions; fall back to full list if empty
          const suggestions: string[] = Array.isArray(res.data.service_suggestions)
            ? res.data.service_suggestions as string[]
            : [];
          if (suggestions.length) {
            this.suggestedServices = suggestions;
            this.hasSuggestedServices = true;
          } else {
            this.suggestedServices = [...this.allPredefinedServices];
            this.hasSuggestedServices = false;
          }
        } else {
          this.suggestedServices = [...this.allPredefinedServices];
          this.hasSuggestedServices = false;
        }
        this._updateFilteredServices();
      },
      error: () => {
        this.isLoadingServices = false;
        this.suggestedServices = [...this.allPredefinedServices];
        this.hasSuggestedServices = false;
        this._updateFilteredServices();
      }
    });
}

/**
 * Search services via API as the user types into the service search input.
 * Uses a debounced Subject (350 ms) with switchMap to cancel stale requests.
 */
onServiceInputChange(): void {
  const searchTerm = this.serviceInput.trim();

  if (!searchTerm) {
    this.serviceSearchMode = false;
    this.servicesList = [];
    this._updateFilteredServices();
    // Push empty string to reset debounce pipeline without firing a request
    this.serviceSearch$.next('');
    return;
  }

  this.serviceSearchMode = true;
  // Delegate to debounced Subject — no direct HTTP call here
  this.serviceSearch$.next(searchTerm);
}

/** Rebuild filteredServices — show all items (selected ones render with checkbox checked) */
private _updateFilteredServices(): void {
  const pool = this.serviceSearchMode ? this.servicesList : this.suggestedServices;
  this.filteredServices = [...pool];
}

/** Open the services dropdown */
openServiceDropdown(): void {
  this.showServiceDropdown = !this.showServiceDropdown;

  // Only fetch suggestions when opening and there is no active search term.
  // If the user had previously typed something, preserve the search results.
  if (this.showServiceDropdown && !this.serviceSearchMode && !this.serviceInput.trim()) {
    this.fetchSuggestedServices();
  }
}

/** Toggle the "View More Services / Show Less" section */
toggleViewMoreServices(): void {
  this.showAllServices = !this.showAllServices;
}

/** Services shown in "View All Services" — the complete predefined list (includes suggested ones too) */
get viewMoreServicesList(): string[] {
  return this.allPredefinedServices;
}

addServiceFromInput(event?: Event) {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  const value = this.serviceInput?.trim();
  if (!value) return;

  const control = this.practiceDetailsForm.get('services_offered');
  const current: string[] = control?.value || [];

  if (!current.includes(value)) {
    control?.setValue([...current, value]);
  }

  this.serviceInput = '';
  this.serviceSearchMode = false;
  this.showServiceDropdown = false;
  this._updateFilteredServices();
}

addServiceFromDropdown(service: string) {
  const control = this.practiceDetailsForm.get('services_offered');
  const current: string[] = control?.value || [];

  if (!current.includes(service)) {
    control?.setValue([...current, service]);
  }

  this.serviceInput = '';
  this.serviceSearchMode = false;
  this.servicesList = [];
  this.showServiceDropdown = false;
  this._updateFilteredServices();
}

/**
 * Toggle a service selection without closing the dropdown — mirrors selectPracticeArea behaviour.
 * Replaces the old addServiceFromDropdown (which closed the dropdown on each pick).
 */
toggleServiceFromDropdown(service: string): void {
  const control = this.practiceDetailsForm.get('services_offered');
  const current: string[] = control?.value || [];

  // if (current.includes(service)) {
  //   control?.setValue(current.filter((s: string) => s !== service));
  // } else {
  //   control?.setValue([...current, service]);
  // }
  const exists = current.some(
    s => s.trim().toLowerCase() === service.trim().toLowerCase()
  );

  if (!exists) {
    control?.setValue([...current, service]);
  }
  this.serviceInput = '';

  this._updateFilteredServices();
  // Keep dropdown open so the user can continue selecting/deselecting
}

/** Handle the "Other" option click — show the free-text input row */
selectOtherService(): void {
  this.showOtherServiceInput = true;
  this.showServiceDropdown = false;
  this.otherServiceValue = '';
  this.otherServiceSuggestions = [];
  this.duplicateServiceMatch = '';
}

/** Add the custom "Other" service value as a chip */
addOtherService(): void {
  const value = this.otherServiceValue?.trim();
  if (!value) return;

  // US-4: Guard against excessively long custom service names
  if (value.length > 200) {
    this.toast.warning('Custom service name must be 200 characters or fewer.');
    return;
  }

  // Change 2: Exact-match duplicate detection against all predefined services
  const exactMatch = this.allPredefinedServices.find(
    (s) => s.trim().toLowerCase() === value.toLowerCase()
  );
  if (exactMatch) {
    // Show duplicate warning and auto-select the existing predefined service
    this.duplicateServiceMatch = exactMatch;
    this.otherServiceSuggestions = [];
    return;
  }

  const control = this.practiceDetailsForm.get('services_offered');
  const current: string[] = control?.value || [];

  // if (!current.includes(value)) {
  //   control?.setValue([...current, value]);
  // }
  // Case-insensitive duplicate check
  const exists = current.some(
    s => s.trim().toLowerCase() === value.trim().toLowerCase()
  );

  if (exists) {
    this.toast.warning('Service already added');
    return;
  }

  control?.setValue([...current, value]);

  this.otherServiceValue = '';
  this.otherServiceSuggestions = [];
  this.duplicateServiceMatch = '';
  this.showOtherServiceInput = false;
}

/** Cancel the "Other" free-text entry */
cancelOtherService(): void {
  this.otherServiceValue = '';
  this.otherServiceSuggestions = [];
  this.duplicateServiceMatch = '';
  this.showOtherServiceInput = false;
}

/** Change 2: Live suggestion matching while user types in the custom-service input */
onOtherServiceInput(): void {
  const term = this.otherServiceValue?.trim().toLowerCase();
  this.duplicateServiceMatch = ''; // clear prior exact-match warning on new input

  if (!term) {
    this.otherServiceSuggestions = [];
    return;
  }

  this.otherServiceSuggestions = this.allPredefinedServices.filter(
    (s) => s.toLowerCase().includes(term)
  );
}

/** Change 2: Select an existing predefined service from the suggestion panel or duplicate warning */
// selectExistingService(service: string): void {
//   const control = this.practiceDetailsForm.get('services_offered');
//   const current: string[] = control?.value || [];
//   if (!current.includes(service)) {
//     control?.setValue([...current, service]);
//   }
//   this.otherServiceValue = '';
//   this.otherServiceSuggestions = [];
//   this.duplicateServiceMatch = '';
//   this.showOtherServiceInput = false;
// }
  selectExistingService(service: string): void {
    const control = this.practiceDetailsForm.get('services_offered');
    const selected: string[] = control?.value || [];

    const exists = selected.some(
      s => s.trim().toLowerCase() === service.trim().toLowerCase()
    );

    if (!exists) {
      control?.setValue([...selected, service]);
    }

    this.cancelOtherService();
  }

removeService(service: string) {
  const control = this.practiceDetailsForm.get('services_offered');
  control?.setValue(control.value.filter((s: string) => s !== service));
}

onServiceBlur() {
  setTimeout(() => {
    this.showServiceDropdown = false;
  }, 200);
}

// ==========================================
// LANGUAGES CHIP INPUT METHODS
// ==========================================

addLanguageFromInput(event?: Event) {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  const value = this.languageInput?.trim();
  if (!value) return;

  const control = this.practiceDetailsForm.get('languages_spoken');
  const current: string[] = control?.value || [];

  if (!current.includes(value)) {
    control?.setValue([...current, value]);
  }

  this.languageInput = '';
  this.showLanguageDropdown = false;
}

addLanguageFromDropdown(lang: string) {
  const control = this.practiceDetailsForm.get('languages_spoken');
  const current: string[] = control?.value || [];

  if (!current.includes(lang)) {
    control?.setValue([...current, lang]);
  }

  this.languageInput = '';
  this.showLanguageDropdown = false;
}
onPracticeAreaSelect(values: string[]) {
  if (values.length > 5) {
    values.pop();
  }
  this.practiceDetailsForm.get('practice_areas')?.setValue(values);
  
}

removeLanguage(lang: string) {
  const control = this.practiceDetailsForm.get('languages_spoken');
  control?.setValue(control.value.filter((l: string) => l !== lang));
}
onLanguageBlur() {
  setTimeout(() => {
    this.showLanguageDropdown = false;
  }, 200);
}

onLanguageInputChange() {
  const searchTerm = this.languageInput.toLowerCase();
  
  if (!searchTerm) {
    this.filteredLanguages = [...this.languagesList];
  } else {
    this.filteredLanguages = this.languagesList.filter(lang =>
      lang.toLowerCase().includes(searchTerm)
    );
  }
  
  const currentLanguages = this.practiceDetailsForm.get('languages_spoken')?.value || [];
  this.filteredLanguages = this.filteredLanguages.filter(
    lang => !currentLanguages.includes(lang)
  );
}
  // Subscription actions
  onUpgrade() {
    this.toast.info('Upgrade functionality coming soon');
  }

  onPayBill() {
    //this.toast.success('Payment processing...');
    this.router.navigate(['/subscribe']);
  }

  onCancelSubscription() {
    if (confirm('Are you sure you want to cancel your subscription?')) {
      this.toast.warning('Cancel subscription functionality coming soon');
    }
  }

  onRenewSubscription() {
    this.toast.info('Renew subscription functionality coming soon');
  }

  // ── Custom court dropdown helpers ─────────────────────────────────────────

  /** Toggle the custom State dropdown for court row i */
  toggleCourtStateDropdown(i: number): void {
    if (this.activeCourtStateDropdown === i) {
      this.activeCourtStateDropdown = null;
    } else {
      this.activeCourtStateDropdown = i;
      this.activeCourtCityDropdown = null;
      this.activeCourtNameDropdown = null;
      this.activeCourtTypeDropdown = null;
      if (!this.courtStateSearch[i]) this.courtStateSearch[i] = '';
    }
  }

  /** Toggle the custom City dropdown for court row i */
  toggleCourtCityDropdown(i: number): void {
    if (this.activeCourtCityDropdown === i) {
      this.activeCourtCityDropdown = null;
    } else {
      this.activeCourtCityDropdown = i;
      this.activeCourtStateDropdown = null;
      this.activeCourtNameDropdown = null;
      this.activeCourtTypeDropdown = null;
      if (!this.courtCitySearch[i]) this.courtCitySearch[i] = '';
    }
  }

  /** Toggle the custom Gender dropdown (Profile Information — solo & firm accounts) */
  toggleGenderDropdown(): void {
    this.isGenderDropdownOpen = !this.isGenderDropdownOpen;
  }

  /** Select a gender value from the custom dropdown */
  selectGender(value: string): void {
    this.personalInfoForm.get('gender')?.setValue(value);
    this.personalInfoForm.get('gender')?.markAsDirty();
    this.isGenderDropdownOpen = false;
  }

  /** Display label for the currently selected gender value */
  get selectedGenderLabel(): string {
    const value = this.personalInfoForm.get('gender')?.value;
    const match = this.genderOptions.find(g => g.value === value);
    return match ? match.label : '';
  }

  /** Toggle the custom Court Type dropdown for court row i */
  toggleCourtTypeDropdown(i: number): void {
    if (this.activeCourtTypeDropdown === i) {
      this.activeCourtTypeDropdown = null;
      this.showAllCourtTypes = null;
    } else {
      this.activeCourtTypeDropdown = i;
      this.activeCourtStateDropdown = null;
      this.activeCourtCityDropdown = null;
      this.activeCourtNameDropdown = null;
      this.showAllCourtTypes = null;
      if (!this.courtTypeSearch[i]) this.courtTypeSearch[i] = '';
      // Always refresh suggestions on open so they reflect the latest practice areas
      this.fetchCourtSuggestions();
    }
  }

  /** Select a court type from custom dropdown */
  selectCourtType(i: number, value: string): void {
    const row = this.courtEnrollments.at(i) as any;
    row.get('court_type').setValue(value);
    this.activeCourtTypeDropdown = null;
    this.showAllCourtTypes = null;
    this.courtTypeSearch[i] = '';
    this.onCourtTypeChange(i);
  }

  /** Toggle between suggested courts and full courts list */
  toggleViewMoreCourtTypes(i: number): void {
    this.showAllCourtTypes = (this.showAllCourtTypes === i) ? null : i;
    // Scroll options list to top after expanding so user sees from the beginning
    if (this.showAllCourtTypes === i) {
      setTimeout(() => {
        const el = document.getElementById('court-options-list-' + i);
        if (el) el.scrollTop = 0;
      }, 0);
    }
  }

  /** Select a state from custom dropdown */
  selectCourtState(i: number, value: string): void {
    const row = this.courtEnrollments.at(i) as any;
    row.get('state').setValue(value);
    this.activeCourtStateDropdown = null;
    this.courtStateSearch[i] = '';
    this.onCourtStateChange(i);
  }

  /** Select a city from custom dropdown */
  selectCourtCity(i: number, value: string): void {
    if (value === '__other__') {
      this.showCityOtherInput[i] = true;
      this.activeCourtCityDropdown = null;
      this.courtCitySearch[i] = '';
      return;
    }
    const row = this.courtEnrollments.at(i) as any;
    row.get('city').setValue(value);
    this.activeCourtCityDropdown = null;
    this.courtCitySearch[i] = '';
    this.showCityOtherInput[i] = false;
  }

  /** Confirm the "Other" city free-text value */
  confirmOtherCity(i: number): void {
    const val = (this.courtCityOtherInput[i] || '').trim();
    if (!val) return;
    const row = this.courtEnrollments.at(i) as any;
    row.get('city').setValue(val);
    this.showCityOtherInput[i] = false;
    this.courtCityOtherInput[i] = '';
  }

  /** Get filtered states for search */
  getFilteredCourtStates(i: number): string[] {
    const all = this.getCourtStates(i);
    const q = (this.courtStateSearch[i] || '').toLowerCase();
    return q ? all.filter(s => s.toLowerCase().includes(q)) : all;
  }

  /** Get filtered cities for search */
  getFilteredCourtCities(i: number): string[] {
    const all = this.getCourtCities(i);
    const q = (this.courtCitySearch[i] || '').toLowerCase();
    return q ? all.filter(c => c.toLowerCase().includes(q)) : all;
  }

  /** Close all court dropdowns (used on outside click) */
  closeAllCourtDropdowns(): void {
    this.activeCourtStateDropdown = null;
    this.activeCourtCityDropdown = null;
    this.activeCourtNameDropdown = null;
    this.activeCourtTypeDropdown = null;
  }

  
  // ═══════════════════════════════════════════════════════════
  //  PLAN MODAL — OPEN / CLOSE
  // ═══════════════════════════════════════════════════════════

  openPlanModal( mode: 'upgrade' | 'downgrade'): void {
    
    // this.selectedProfessional = professional;
    this.planModalMode        = mode;
    this.planModalData        = null;
    this.planOptions          = [];
    this.selectedPlanKey      = '';
    this.downgradeReason      = '';
    this.downgradeReasonError = '';
    this.showPlanModal        = true;
    this.planModalLoading     = true;

    this.httpservice.sendGetRequest(URLUtils.paymentPlan).subscribe(
      (response: any) => {
        if (response?.data) {
          this.planModalData = response.data;
          this.planOptions   = mode === 'upgrade'
            ? (response.data.upgrade_options   || [])
            : (response.data.downgrade_options || []);
          if (this.planOptions.length > 0) {
            this.selectedPlanKey = this.planOptions[0].key;
          }
        } else {
          this.toast.error('Failed to load plan options', 'Error');
          this.showPlanModal = false;
        }
        this.planModalLoading = false;
      },
      () => {
        this.toast.error('Failed to load plan options', 'Error');
        this.planModalLoading = false;
        this.showPlanModal    = false;
      }
    );
  }

  closePlanModal(): void {
    this.showPlanModal        = false;
    this.planModalData        = null;
    this.planOptions          = [];
    this.selectedPlanKey      = '';
    this.planModalMode        = null;
    // this.selectedProfessional = null;
    this.downgradeReason      = '';
    this.downgradeReasonError = '';
  }

  // ═══════════════════════════════════════════════════════════
  //  PLAN MODAL — SUBMIT
  // ═══════════════════════════════════════════════════════════

  submitPlanChange(): void {
    if ( !this.selectedPlanKey) return;

    if (this.planModalMode === 'downgrade' && !this.downgradeReason.trim()) {
      this.downgradeReasonError = 'Reason is required for downgrade';
      return;
    }

    this.isPlanSubmitting = true;
    this.spinnerService.show();
   
    const uid      = this.profuid;
    const request$ = this.planModalMode === 'upgrade'
      ? this.httpservice.sendPostRequest(URLUtils.PlanUpgrade,  { new_plan: this.selectedPlanKey })
      : this.httpservice.sendPostRequest(URLUtils.PlanDownGrade, { new_plan: this.selectedPlanKey ,reason:this.downgradeReason.trim()});

    request$.subscribe(
      (response: any) => {
        if (response && !response.error) {
          this.toast.success(response.msg || 'Plan updated successfully', 'Success');
          this.closePlanModal();
        } else {
          this.toast.error(response?.msg || 'Failed to update plan', 'Error');
        }
        this.isPlanSubmitting = false;
        this.spinnerService.hide();
      },
      (error:any) => {
        this.toast.error(error?.error?.msg || 'Failed to update plan', 'Error');
        this.isPlanSubmitting = false;
        this.spinnerService.hide();
      }
    );
  }

  getSelectedPlanPricing(): any {
    return this.planOptions.find(p => p.key === this.selectedPlanKey)?.pricing || null;
  }

  // ═══════════════════════════════════════════════════════════
  //  BANK ACCOUNT DETAILS — Subscription Details tab
  // ═══════════════════════════════════════════════════════════

  /** Group-level validator: keeps Confirm Account Number in sync with Account Number in real time */
  // private accountNumbersMatchValidator(group: AbstractControl): { [key: string]: boolean } | null {
  //   const accCtrl = group.get('account_number');
  //   const confirmCtrl = group.get('confirm_account_number');
  //   if (!accCtrl || !confirmCtrl) return null;

  //   const accNum = accCtrl.value;
  //   const confirmNum = confirmCtrl.value;

  //   if (accNum && confirmNum && accNum !== confirmNum) {
  //     confirmCtrl.setErrors({ ...(confirmCtrl.errors || {}), mismatch: true });
  //     return { accountNumberMismatch: true };
  //   }

  //   if (confirmCtrl.hasError('mismatch')) {
  //     const remaining = { ...confirmCtrl.errors };
  //     delete remaining['mismatch'];
  //     confirmCtrl.setErrors(Object.keys(remaining).length ? remaining : null);
  //   }
  //   return null;
  // }
  private accountNumbersMatchValidator(group: AbstractControl): { [key: string]: boolean } | null {
  const accountNumber = group.get('account_number')?.value;
  const confirmAccountNumber = group.get('confirm_account_number')?.value;

  if (!accountNumber || !confirmAccountNumber) {
    return null;
  }

  if (accountNumber !== confirmAccountNumber) {
    return { accountNumberMismatch: true };
  }

  return null;
}

  initBankAccountForm(): void {
    this.bankAccountForm = this.fb.group({
      account_holder_name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100), Validators.pattern(this.accountHolderNamePattern)]],
      bank_name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100), Validators.pattern(this.bankNamePattern)]],
      account_number: ['', [Validators.required, Validators.pattern(this.accountNumberPattern)]],
      confirm_account_number: ['', [Validators.required, Validators.pattern(this.accountNumberPattern)]],
      ifsc_code: ['', [Validators.required, Validators.pattern(this.ifscPattern)]],
      branch_name: [{ value: '', disabled: true }]
    }, { validators: this.accountNumbersMatchValidator });

    // Auto-uppercase IFSC as the user types
    this.bankAccountForm.get('ifsc_code')?.valueChanges.subscribe((val: string) => {
      if (val && val !== val.toUpperCase()) {
        this.bankAccountForm.get('ifsc_code')?.setValue(val.toUpperCase(), { emitEvent: false });
      }
    });

    // Any edit to verification-sensitive fields invalidates a prior successful verification
    ['bank_name', 'account_number', 'confirm_account_number', 'ifsc_code'].forEach(name => {
      this.bankAccountForm.get(name)?.valueChanges.subscribe(() => {
        if (this.isBankAccountVerified && !this.isBankFieldsLocked) {
          this.isBankAccountVerified = false;
          this.bankVerifyMessage = '';
        }
      });
    });
  }

  /** Whether the Verify button should be enabled */
  get canVerifyBankAccount(): boolean {
    const f = this.bankAccountForm;
    if (!f || this.isBankAccountVerifying || this.isBankFieldsLocked) return false;
    return !!(
      f.get('account_number')?.valid &&
      f.get('confirm_account_number')?.valid &&
      !f.hasError('accountNumberMismatch') &&
      f.get('ifsc_code')?.valid
    );
  }

  /** Whether Submit should be enabled */
  get isBankSubmitEnabled(): boolean {
    return !!(this.bankAccountForm?.valid && this.isBankAccountVerified && this.bankTermsAccepted && !this.isBankAccountSubmitting);
  }

  /** Strips non-digit characters as the user types (Account Number / Confirm Account Number) */
  sanitizeNumericInput(controlName: 'account_number' | 'confirm_account_number'): void {
    const ctrl = this.bankAccountForm.get(controlName);
    if (!ctrl) return;
    const cleaned = (ctrl.value || '').toString().replace(/\D/g, '').slice(0, 18);
    if (cleaned !== ctrl.value) {
      ctrl.setValue(cleaned);
    }
  }

  /** Prevents Enter from submitting the Bank Account form while typing in a text field,
   *  while still letting Enter/Space activate a focused button (e.g. Verify). */
  blockEnterOnInput(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (target && target.tagName === 'INPUT') {
      event.preventDefault();
    }
  }

  toggleAccountNumberVisibility(): void {
    this.showAccountNumber = !this.showAccountNumber;
  }

  toggleConfirmAccountNumberVisibility(): void {
    this.showConfirmAccountNumber = !this.showConfirmAccountNumber;
  }

  /** GET /v3/bank-account — called once per Subscription Details tab load (see
   *  switchTab / handleCompleteNow) and again after a successful save, so the
   *  Subscription Details card and the dialog's View mode always reflect the
   *  latest saved record without reloading the whole page. */
  loadBankAccountDetails(): void {
    // Safety net: subscription_details.bank_account (from the main extendedProfile
    // call) is the source that reliably reflects an already-saved bank account.
    // If the dedicated GET below is unavailable, errors, or returns a shape we
    // don't recognise, we fall back to it instead of incorrectly showing "Add".
    const profileFallback = this.subscription_details?.bank_account || null;

    this.httpservice.sendGetRequest(URLUtils.bankAccountGet).subscribe({
      next: (res: any) => {
        this.bankAccountFetched = true;
        if (res?.error === true) {
          this.bankAccountDetails = profileFallback;
          return;
        }
        let data = res?.data?.account_details ?? res?.data?.bank_account ?? res?.data ?? null;
        if (Array.isArray(data)) {
          data = data[0] || null;
        }
        const hasAccount = !!(data && typeof data === 'object' && Object.keys(data).length &&
          (data.account_number || data.ifsc_code || data.bank_name || data.account_holder_name));
        if (hasAccount) {
          // Normalize the API's `verification_status` into the `is_verified`
          // flag the rest of this component (View badge, Edit-mode locking)
          // already reads — the original field is kept alongside it.
          data = { ...data, is_verified: data.is_verified ?? data.verification_status };
        }
        this.bankAccountDetails = hasAccount ? data : profileFallback;
      },
      error: () => {
        // The dedicated endpoint may 404 or not be wired up in every environment —
        // that alone doesn't mean the user has no bank account on file.
        this.bankAccountFetched = true;
        this.bankAccountDetails = profileFallback;
      }
    });
  }

  /** Masked Account Number for View mode display only, e.g. "**********9012".
   *  The real value never gets a chance to render in View mode. */
  get maskedBankAccountNumber(): string {
    const num = (this.bankAccountDetails?.account_number || '').toString();
    if (!num) return '';
    if (num.length <= 4) return num;
    return '*'.repeat(num.length - 4) + num.slice(-4);
  }

  /** Opens the modal. mode='view' shows the read-only, masked details fetched
   *  via GET /v3/bank-account. mode='add' opens a blank form as before. */
  openBankAccountModal(mode: 'add' | 'view' = 'add'): void {
    this.resetBankAccountForm();
    this.bankAccountModalMode = mode;

    const holderName = this.basic_profile?.name || '';
    this.bankAccountForm.get('account_holder_name')?.setValue(holderName || '');

    this.showBankAccountModal = true;
  }

  /** Switches the already-open dialog from View mode into Edit mode in place —
   *  no second dialog is opened. Pre-fills and locks the form exactly like a
   *  freshly-verified Add flow, matching the existing verify/lock/unlock logic. */
  switchToEditMode(): void {
    const existing = this.bankAccountDetails;
    if (!existing) return;

    this.resetBankAccountForm();
    this.bankAccountModalMode = 'edit';

    const holderName = this.basic_profile?.name || '';
    this.bankAccountForm.get('branch_name')?.enable({ emitEvent: false });
    this.bankAccountForm.patchValue({
      account_holder_name: existing.account_holder_name || holderName || '',
      bank_name: existing.bank_name || '',
      account_number: existing.account_number || '',
      confirm_account_number: existing.account_number || '',
      ifsc_code: existing.ifsc_code || '',
      branch_name: existing.branch_name || ''
    });

    if (existing.is_verified !== false) {
      this.isBankAccountVerified = true;
      this.bankVerifyMessage = 'Bank account verified successfully.';
      this.lockVerifiedBankFields();
    }
    this.bankTermsAccepted = true;

    // Snapshot the just-populated state so Cancel/Close can tell a real edit
    // apart from simply entering and leaving Edit mode untouched.
    this.bankFormSnapshot = { ...this.bankAccountForm.getRawValue(), __terms: this.bankTermsAccepted };
  }

  /** True when the user has actually changed something while in Edit mode. */
  private isBankFormDirty(): boolean {
    if (!this.bankFormSnapshot) return false;
    const current = { ...this.bankAccountForm.getRawValue(), __terms: this.bankTermsAccepted };
    return JSON.stringify(current) !== JSON.stringify(this.bankFormSnapshot);
  }

  /** Entry point for the X button, backdrop click, and footer Cancel button.
   *  Only Edit mode with unsaved changes is gated behind a confirmation —
   *  Add mode and View mode close immediately, exactly as before. */
  requestCloseBankAccountModal(): void {
    if (this.isBankAccountSubmitting) return;
    if (this.bankAccountModalMode === 'edit' && this.isBankFormDirty()) {
      this.showBankDiscardConfirm = true;
      return;
    }
    this.closeBankAccountModal();
  }

  keepEditingBankAccount(): void {
    this.showBankDiscardConfirm = false;
  }

  confirmDiscardBankChanges(): void {
    this.showBankDiscardConfirm = false;
    this.closeBankAccountModal();
  }

  closeBankAccountModal(): void {
    if (this.isBankAccountSubmitting) return;
    this.showBankAccountModal = false;
    this.showBankDiscardConfirm = false;
  }

  resetBankAccountForm(): void {
    this.isBankAccountVerifying = false;
    this.isBankAccountSubmitting = false;
    this.isBankAccountVerified = false;
    this.isBankFieldsLocked = false;
    this.bankVerifyMessage = '';
    this.bankVerifyError = '';
    this.bankTermsAccepted = false;
    this.showAccountNumber = false;
    this.showConfirmAccountNumber = false;
    this.bankAccountModalMode = 'add';
    this.showBankDiscardConfirm = false;
    this.bankFormSnapshot = null;

    this.bankAccountForm.reset();
    ['account_holder_name', 'bank_name', 'account_number', 'confirm_account_number', 'ifsc_code'].forEach(name => {
      this.bankAccountForm.get(name)?.enable({ emitEvent: false });
    });
    this.bankAccountForm.get('branch_name')?.disable({ emitEvent: false });
  }

  /** Locks only the fields tied to IFSC verification. Bank Name/Branch Name stay editable
   *  after a successful verify, per the approved UX (user may correct them if needed). */
  private lockVerifiedBankFields(): void {
    this.isBankFieldsLocked = true;
    ['account_number', 'confirm_account_number', 'ifsc_code'].forEach(name => {
      this.bankAccountForm.get(name)?.disable({ emitEvent: false });
    });
  }

  /** Lets the user unlock and re-verify an already-verified set of bank details */
  unlockBankFieldsForEdit(): void {
    this.isBankFieldsLocked = false;
    this.isBankAccountVerified = false;
    this.bankVerifyMessage = '';
    this.bankVerifyError = '';
    this.bankAccountForm.get('branch_name')?.setValue('');
    this.bankAccountForm.get('branch_name')?.disable({ emitEvent: false });
    ['account_number', 'confirm_account_number', 'ifsc_code'].forEach(name => {
      this.bankAccountForm.get(name)?.enable({ emitEvent: false });
    });
  }

  verifyBankAccount(): void {
    if (this.isBankAccountVerifying || this.isBankFieldsLocked) return;

    this.bankVerifyError = '';

    // Step 1: local mismatch check — do NOT call the API on mismatch
    const accNum = this.bankAccountForm.get('account_number')?.value;
    const confirmNum = this.bankAccountForm.get('confirm_account_number')?.value;
    if (accNum !== confirmNum) {
      this.bankAccountForm.get('confirm_account_number')?.markAsTouched();
      this.bankVerifyError = 'Account numbers do not match.';
      return;
    }

    if (!this.canVerifyBankAccount) {
      this.bankAccountForm.markAllAsTouched();
      return;
    }

    const ifsc = (this.bankAccountForm.get('ifsc_code')?.value || '').toUpperCase();

    this.isBankAccountVerifying = true;
    this.isBankAccountVerified = false;
    this.bankVerifyMessage = '';

    // Replaces the previous Razorpay IFSC lookup — now calls our own backend
    // via the existing HttpService (environment.apiUrl + auth headers are
    // already applied inside sendGetRequest, matching the rest of this file).
    const verifyUrl = URLUtils.bankAccountVerify.replace('{IFSC_CODE}', ifsc);

    this.httpservice.sendGetRequest(verifyUrl).subscribe({
      next: (res: any) => {
        this.isBankAccountVerifying = false;

        const data = res?.data ?? res;
        const bankName = data?.bank_name || '';
        const branchName = data?.branch_name || '';

        // Reactive form update: only bankName / branchName are ever patched here —
        // accountNumber, confirmAccountNumber, and accountHolderName are left untouched.
        if (bankName) {
          this.bankAccountForm.get('bank_name')?.setValue(bankName);
        }
        this.bankAccountForm.get('branch_name')?.enable({ emitEvent: false });
        this.bankAccountForm.get('branch_name')?.setValue(branchName);

        this.isBankAccountVerified = true;
        this.bankVerifyMessage = 'Bank account verified successfully.';
        this.lockVerifiedBankFields();
      },
      error: (error: HttpErrorResponse) => {
        this.isBankAccountVerifying = false;
        this.isBankAccountVerified = false;
        this.bankAccountForm.get('branch_name')?.setValue('');
        this.bankAccountForm.get('branch_name')?.disable({ emitEvent: false });

        this.bankVerifyError = error?.error?.msg || error?.error?.message || 'Bank account verification failed. Please try again.';
      }
    });
  }

  submitBankAccountDetails(): void {
    if (!this.isBankSubmitEnabled || this.isBankAccountSubmitting) return;

    this.isBankAccountSubmitting = true;
    const raw = this.bankAccountForm.getRawValue();
    // Confirm Account Number is intentionally excluded — backend contract
    // only accepts the fields below.
    const payload = {
      account_holder_name: raw.account_holder_name,
      account_number: raw.account_number,
      ifsc_code: raw.ifsc_code,
      bank_name: (raw.bank_name || '').trim(),
      branch_name: raw.branch_name,
      is_verified: true
    };

    this.httpservice.sendPostRequest(URLUtils.bankAccountAdd, payload).subscribe(
      (res: any) => {
        this.isBankAccountSubmitting = false;
        if (res && res.error === false) {
          const defaultMsg = this.bankAccountModalMode === 'edit'
            ? 'Bank account details updated successfully.'
            : 'Bank account details added successfully.';
          this.toast.success(res.msg || defaultMsg, 'Success');
          this.closeBankAccountModal();
          this.getData(); // refresh Subscription Details so the "already added" state reflects the saved details
          this.loadBankAccountDetails(); // refresh the dedicated bank-account record backing the View/Edit dialog
        } else {
          this.toast.error(res?.msg || 'Unable to save bank account details. Please try again.', 'Error');
        }
      },
      (error: HttpErrorResponse) => {
        this.isBankAccountSubmitting = false;
        this.toast.error(error?.error?.msg || error?.error?.message || 'Unable to save bank account details. Please try again.', 'Error');
      }
    );
  }

  enableBankNameInput(event: FocusEvent): void {
  const input = event.target as HTMLInputElement;

  if (this.bankAccountModalMode !== 'edit') {
  if (input.readOnly) {
    input.readOnly = false;

    // Remove any browser autofill that happened before focus
    const control = this.bankAccountForm.get('bank_name');

    if (control?.value) {
      control.setValue('', { emitEvent: false });
    }
  }
  }
}

enableAccountNumberInput(event: FocusEvent): void {
  const input = event.target as HTMLInputElement;
  // console.log('this.bankAccountModalMode',this.bankAccountModalMode)

  if (this.bankAccountModalMode !== 'edit') {
    if (input.readOnly) {
      input.readOnly = false;

      // Remove any browser/password-manager autofill
      const control = this.bankAccountForm.get('account_number');

      if (control?.value) {
        control.setValue('', { emitEvent: false });
      }
    }
  }
}

enableConfirmAccountNumberInput(event: FocusEvent): void {
  const input = event.target as HTMLInputElement;

  if (this.bankAccountModalMode !== 'edit') {
    if (input.readOnly) {
      input.readOnly = false;

      // Remove any browser/password-manager autofill
      const control = this.bankAccountForm.get('confirm_account_number');

      if (control?.value) {
        control.setValue('', { emitEvent: false });
      }
    }
  }
}
}